import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings, Eye, CheckCircle, Search, Trash2, ChevronUp, ChevronDown, ChevronLeft, HelpCircle,
  CheckSquare, XCircle, Code, AlignLeft, Image, LayoutGrid, Type, Plus, Home,
  Calculator, MessageSquare, ImageIcon, AlignCenter, AlignJustify, ListOrdered, List, Quote, Table,
  Minus, Hash, Paperclip, Undo, Redo, Copy, ArrowUp, ArrowDown,
  GripVertical, X, ChevronRight, Clock, AlertCircle, Check, Star, Edit3, MinusCircle,
  Settings2, EyeOff, Trash, Save, Upload, Code2, Lightbulb,
  Eraser, Pencil
} from 'lucide-react';
import 'react-quill/dist/quill.snow.css';
import { categoryStore, Organization, CourseCategory, Level } from '../../store/categoryStore';
import ImageUploadModal from '../../components/ImageUploadModal';
import ImageUploadDialog from '../../components/ImageUploadDialog';
import QuizStyleQuestionView from '../../components/admin/QuizStyleQuestionView';
import InsertQuestionModal from '../../components/admin/InsertQuestionModal';
import AnswerCard from '../../pages/quiz/components/AnswerCard';
import QuizAnswerMode from '../../pages/quiz/QuizAnswerMode';
import paperStorage from '../../utils/paperStorage';
import { supabase, getSupabaseUrl } from '../../supabase/client';

interface Question {
  id: string;
  type: string;
  subType?: 'scratch' | 'python' | 'cpp';
  title?: string; // 题目名称（所有题型统一）
  content?: string; // 题干内容（富文本）
  placeholder?: string;
  required: boolean;
  inputType?: string;
  options: Option[];
  score: number;
  showAnswer?: boolean;
  answerAnalysis?: string;
  titleImages: string[];
  titleImageWidths?: number[];
  imagePosition?: 'above' | 'below';
  titleImageLayout?: string;
  optionLayout?: string;
  answerImages?: string[];
  answerImageWidths?: number[];
  codeTemplate?: string;
  codeLanguage?: string;
  allowImageUpload?: boolean;
  maxImageSize?: number;
  allowTextInput?: boolean;
  maxTextLength?: number;
  allowFileUpload?: boolean;
  maxFileSize?: number;
  gradingMode?: 'manual' | 'ai';
  scoringPoints?: string;
  explanation?: string;
  answerText?: string;
  scoringPointsImages?: string[];
  scoringPointsImageWidths?: number[];
  referenceAnswerImages?: string[];
  referenceAnswerImageWidths?: number[];
  graphicsAnswerItems?: GraphicsAnswerItem[];
  graphicsDifficulty?: 'easy' | 'medium' | 'hard';
  graphicsCategory?: string;
  graphicsContent?: string;
  scratchAnswerItems?: ScratchAnswerItem[];
  // Scratch三种题型配置
  scratchSubtype?: ScratchQuestionSubtype;
  scratchConfig?: AlgorithmConfig | OperationConfig | CreativeConfig;
  referenceSb3Url?: string;
  optionImageWidths?: Record<string, number>;
  // Python编辑器新增字段
  difficulty?: 'easy' | 'medium' | 'hard';
  knowledgePointIds?: string[];
  commonMistakes?: string;
  timeLimitMs?: number;
  memoryLimitMb?: number;
  testCases?: any[];
  structuredScoring?: {
    points: { id: string; name: string; score: number }[];
    commonMistakes: string;
  };
}

interface ScratchAnswerItem {
  id: string;
  name: string;
  placeholder: string;
  required: boolean;
  maxSizeMB: number;
  aiReferenceImage?: string;
  aiReferenceImageWidth?: number;
  aiScoringPoints?: string;
}

interface Option {
  id: string;
  label: string;
  text: string;
  isCorrect: boolean;
  // 旧字段保留兼容
  image?: string;        // 单张图片(向后兼容)
  imageWidth?: number;   // 单张图片宽度

  // 新字段:多张图片支持
  images?: string[];           // 图片URL数组
  imageWidths?: number[];      // 对应每张图片的宽度百分比数组
}

interface GraphicsAnswerItem {
  id: string;
  name: string;
  placeholder: string;
  required: boolean;
  maxSizeMB: number;
  maxCount: number;
  aiReferenceImage?: string;
  aiScoringPoints?: string;
}

interface ImageUploadTarget {
  questionId: string;
  optionId?: string;
  existingImageIndex?: number;
}

interface ImageViewerState {
  isOpen: boolean;
  imageUrl: string;
  title: string;
  currentIndex: number;
  totalCount: number;
}

const questionTypes = [
  {
    category: '考试题型',
    items: [
      { icon: CheckSquare, label: '单选题', type: 'single_choice', placeholder: '请选择' },
      { icon: CheckCircle, label: '判断题', type: 'judge', placeholder: '请选择' },
      { icon: AlignLeft, label: '多选题', type: 'multi_choice', placeholder: '请选择' },
      { icon: AlignLeft, label: '主观题', type: 'subjective', placeholder: '请输入答案' },
      { icon: Code2, label: 'Scratch编程题', type: 'scratch_coding', placeholder: '请输入代码', scratchSubtype: 'algorithm' as ScratchQuestionSubtype },
      { icon: Code, label: 'Python编程题', type: 'python_coding', placeholder: '请输入代码' },
      { icon: Code, label: 'C++编程题', type: 'cpp_coding', placeholder: '请输入代码' }
    ]
  }
];

// 获取题型标签
const getQuestionTypeLabel = (type: string) => {
  const typeMap: Record<string, string> = {
    'single_choice': '单选题',
    'multi_choice': '多选题',
    'judge': '判断题',
    'subjective': '主观题',
    'python_coding': 'Python编程题',
    'cpp_coding': 'C++编程题',
    'scratch_coding': 'Scratch编程题'
  };
  return typeMap[type] || '未知题型';
};

// 获取题型图标
const getQuestionTypeIcon = (type: string) => {
  const iconMap: Record<string, any> = {
    'single_choice': CheckSquare,
    'multi_choice': AlignLeft,
    'judge': CheckCircle,
    'subjective': AlignLeft,
    'python_coding': Code,
    'cpp_coding': Code,
    'scratch_coding': Code2
  };
  return iconMap[type] || HelpCircle;
};

interface PaperEditorProps {
  isTeacherMode?: boolean;
}

// 从HTML中提取所有<img>标签的src和宽度，返回 {urls, widths}
// 使用 DOMParser 解析，比正则更稳定，能正确处理嵌套属性、单引号、自闭合标签等情况
function extractImagesFromHtml(html: string): { urls: string[]; widths: number[] } {
  if (!html || typeof window === 'undefined') return { urls: [], widths: [] };
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const imgs = Array.from(doc.querySelectorAll('img'));
    const urls: string[] = [];
    const widths: number[] = [];
    imgs.forEach(img => {
      const src = img.getAttribute('src');
      if (!src) return;
      urls.push(src);
      // 优先从 style 中提取 width 百分比
      const style = img.getAttribute('style') || '';
      const widthMatch = style.match(/width:\s*(\d+)%/);
      const width = widthMatch ? parseInt(widthMatch[1]) : 100;
      widths.push(width);
    });
    return { urls, widths };
  } catch {
    // 解析失败时回退到空结果，避免抛错中断保存流程
    return { urls: [], widths: [] };
  }
}

export default function PaperEditor({ isTeacherMode: propIsTeacherMode }: PaperEditorProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editPaperId = searchParams.get('edit');
  const pendingPaperId = searchParams.get('pending');
  const targetQuestionId = searchParams.get('question');
  const fromPage = searchParams.get('from');

  // 优先使用传入的 prop，否则从 URL 判断
  const actualIsTeacherMode = propIsTeacherMode ?? location.pathname.startsWith('/teacher');
  const papersTable = actualIsTeacherMode ? 'teacher_papers' : 'papers';
  const questionsTable = actualIsTeacherMode ? 'teacher_questions' : 'questions';
  const pendingPapersTable = actualIsTeacherMode ? 'teacher_pending_papers' : 'pending_papers';

  const [isPendingPaper, setIsPendingPaper] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [remark, setRemark] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('type');
  const [showScoreDropdown, setShowScoreDropdown] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUploadTarget, setImageUploadTarget] = useState<ImageUploadTarget | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [imageWidth, setImageWidth] = useState(100);
  const [imageViewer, setImageViewer] = useState<ImageViewerState>({ isOpen: false, imageUrl: '', title: '', currentIndex: 0, totalCount: 0 });
  const [showImageUploadDialog, setShowImageUploadDialog] = useState(false);
  const [imageUploadDialogConfig, setImageUploadDialogConfig] = useState<{
    title: string;
    target: 'title' | 'answer' | 'scoringPoints' | 'referenceAnswer';
    questionId: string;
  } | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [saveConfirmAction, setSaveConfirmAction] = useState<'draft' | 'saved' | 'published'>('draft');
  const [isSaving, setIsSaving] = useState(false);
  const [savingAction, setSavingAction] = useState<'draft' | 'saved' | 'published' | null>(null);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const unmountedRef = useRef(false);
  // 题目名称自动保存防抖定时器
  const titleSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draggedQuestion, setDraggedQuestion] = useState<Question | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Quiz风格预览模式相关状态
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasWidthChanges, setHasWidthChanges] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [isSavingWidths, setIsSavingWidths] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  // 插入题目弹窗相关状态
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [insertTargetQuestionId, setInsertTargetQuestionId] = useState<string | null>(null);
  // 复制题目确认弹窗相关状态
  const [showCopyConfirmModal, setShowCopyConfirmModal] = useState(false);
  const [showPaperSettingsModal, setShowPaperSettingsModal] = useState(false);
  const [copyTargetQuestionId, setCopyTargetQuestionId] = useState<string | null>(null);

  // 插入题目确认处理
  const handleInsertConfirm = (position: 'before' | 'after') => {
    if (!insertTargetQuestionId) return;
    const targetQuestion = questions.find(q => q.id === insertTargetQuestionId);
    if (!targetQuestion) return;

    const questionType = targetQuestion.type;
    const newQuestion: Question = {
      id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: questionType,
      title: '',
      content: '',
      placeholder: questionType === 'scratch_coding' ? '请输入代码' : '请选择',
      required: true,
      options: [],
      score: targetQuestion.score,
      showAnswer: false,
      titleImages: [],
      imagePosition: 'above',
      titleImageLayout: 'default',
      optionLayout: 'list',
    };

    // 根据题型设置默认选项
    if (questionType === 'single_choice') {
      newQuestion.options = [
        { id: 'A', label: 'A', text: '', isCorrect: false },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false },
      ];
    } else if (questionType === 'judge') {
      newQuestion.options = [
        { id: 'A', label: 'A', text: '正确', isCorrect: false },
        { id: 'B', label: 'B', text: '错误', isCorrect: false },
      ];
    } else if (questionType === 'multi_choice') {
      newQuestion.options = [
        { id: 'A', label: 'A', text: '', isCorrect: false },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false },
      ];
    } else if (questionType === 'subjective') {
      newQuestion.placeholder = '请输入答案';
    } else if (questionType === 'python_coding') {
      newQuestion.placeholder = '请输入Python代码';
    } else if (questionType === 'cpp_coding') {
      newQuestion.placeholder = '请输入C++代码';
    } else if (questionType === 'scratch_coding') {
      newQuestion.placeholder = '请输入代码';
      newQuestion.scratchSubtype = 'algorithm';
    }

    // 根据位置参数决定插入位置
    const targetIndex = questions.findIndex(q => q.id === insertTargetQuestionId);
    const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;

    const newQuestions = [
      ...questions.slice(0, insertIndex),
      newQuestion,
      ...questions.slice(insertIndex)
    ];

    setQuestions(newQuestions);
    setCurrentIndex(insertIndex);
    setShowInsertModal(false);
    setInsertTargetQuestionId(null);
  };

  // 复制题目确认处理
  const handleCopyConfirm = () => {
    if (!copyTargetQuestionId) return;
    const targetIndex = questions.findIndex(q => q.id === copyTargetQuestionId);
    if (targetIndex === -1) return;
    const targetQuestion = questions[targetIndex];
    const copiedQuestion: Question = JSON.parse(JSON.stringify(targetQuestion));
    copiedQuestion.id = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const newQuestions = [...questions.slice(0, targetIndex + 1), copiedQuestion, ...questions.slice(targetIndex + 1)];
    setQuestions(newQuestions);
    setCurrentIndex(targetIndex + 1);
    setShowCopyConfirmModal(false);
    setCopyTargetQuestionId(null);
  };

  const [paperSettings, setPaperSettings] = useState({
    year: '',
    month: '',
    organization: '',
    source: '',
    nature: '付费',
    categoryType: '',
    level_text: '',
    paperType: '',
    duration: '60'
  });

  const generateAutoTitle = useCallback(() => {
    const { year, month, organization, categoryType, level_text, source } = paperSettings;
    if (!year || !organization || !categoryType || !level_text || !source) {
      return '';
    }
    const monthPart = month && month !== '0' ? `${month}月` : '';
    const categoryName = categoryStore.getCategoryNameByCode(categoryType) || categoryType;
    return `${year}年${monthPart}${organization}${categoryName}${level_text}${source}`;
  }, [paperSettings]);

  // 组件卸载时标记，防止异步回调（setTimeout等）访问已移除的 DOM
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // 试卷属性变化时自动更新标题
  useEffect(() => {
    const autoTitle = generateAutoTitle();
    if (autoTitle) {
      setTitle(autoTitle);
    }
  }, [paperSettings, generateAutoTitle]);

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [cats, setCats] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);

  const selectedOrg = orgs.find(o => o.name === paperSettings.organization);
  const filteredCats = selectedOrg
    ? cats.filter(c => c.orgId === selectedOrg.id)
    : [];

  const selectedCat = filteredCats.find(c => c.name === paperSettings.categoryType);
  const filteredLevels = selectedCat
    ? levels.filter(l => l.catId === selectedCat.id)
    : [];

  const years = ['', ...Array.from({ length: 21 }, (_, i) => String(2010 + i))];
  const months = [{ value: 0, label: '无' }, ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `${i + 1}月` }))];
  const sources = ['真题', '模拟题'];
  const natures = ['免费', '付费'];
  const paperTypes = ['练习', '考试'];

  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const [draftId, setDraftId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const draft = params.get('draft');
    if (draft) setDraftId(draft);
  }, []);

  useEffect(() => {
    fetchCategories();
    if (editPaperId) {
      loadPaperForEdit(editPaperId);
    } else if (pendingPaperId) {
      loadPendingPaperData(pendingPaperId);
    } else if (draftId) {
      loadPaperDraftData(draftId);
    } else {
      // temp_imported_papers 和 temp_graphics_questions 表尚未创建，暂时跳过查询避免 404
      // loadImportedDataFromSupabase();
      // loadGraphicsQuestionFromSupabase();
      loadEditorStateFromStorage();
    }
  }, [editPaperId, pendingPaperId, draftId]);

  const loadImportedDataFromSupabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const result: any = await supabase
        .from('temp_imported_papers' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const data = result.data;
      const error = result.error;
      if (error) {
        // 表不存在时静默跳过，不产生 404 错误日志
        if (error.code === '42P01' || error.message?.includes('does not exist') || (error as any).status === 404) return;
        throw error;
      }
      if (data) {
        loadImportedPaperData((data as any).paper_data);
        await supabase.from('temp_imported_papers' as any).delete().eq('id', (data as any).id);
      }
    } catch (e) {
      console.error('加载导入数据失败:', e);
    }
  };

  const loadEditorStateFromStorage = () => {
    try {
      const savedState = localStorage.getItem('paperEditorState');
      if (savedState) {
        const state = JSON.parse(savedState);
        if (state.title) setTitle(state.title);
        if (state.description) setDescription(state.description);
        if (state.questions && state.questions.length > 0) setQuestions(state.questions);
        if (state.paperSettings) setPaperSettings(state.paperSettings);
        if (state.selectedCategory) setSelectedCategory(state.selectedCategory);
        if (state.isPendingPaper) setIsPendingPaper(state.isPendingPaper);
        if (state.pendingPaperId) setIsPendingPaper(state.pendingPaperId);
        if (state.editPaperId) setIsPendingPaper(state.editPaperId);
        localStorage.removeItem('paperEditorState');
      }

      // 从预览界面回传的图片宽度调整数据
      const widthUpdatesStr = localStorage.getItem('previewImageWidthUpdates');
      if (widthUpdatesStr) {
        const widthUpdates = JSON.parse(widthUpdatesStr);
        if (widthUpdates && Array.isArray(widthUpdates) && widthUpdates.length > 0) {
          setQuestions(prevQuestions => {
            const updatedQuestions = prevQuestions.map(q => {
              const update = widthUpdates.find(u => u.id === q.id);
              if (!update) return q;

              const updatedQ = { ...q };

              // 同步题干图片宽度
              if (update.titleImageWidths && update.titleImageWidths.length > 0) {
                updatedQ.titleImageWidths = update.titleImageWidths;
              }

              // 同步选项图片宽度
              if (update.optionImageWidths && Array.isArray(update.optionImageWidths)) {
                const updatedOptions = q.options.map(opt => {
                  const optUpdate = update.optionImageWidths.find(ow => ow.id === opt.id);
                  if (optUpdate && optUpdate.imageWidth !== undefined) {
                    return { ...opt, imageWidth: optUpdate.imageWidth };
                  }
                  return opt;
                });
                updatedQ.options = updatedOptions;
              }

              return updatedQ;
            });
            return updatedQuestions;
          });
        }
        localStorage.removeItem('previewImageWidthUpdates');
      }
    } catch (e) {
      console.error('加载编辑器状态失败:', e);
    }
  };

  const loadGraphicsQuestionFromSupabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const result: any = await supabase
        .from('temp_graphics_questions' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const data = result.data;
      const error = result.error;
      if (error) {
        // 表不存在时静默跳过，不产生 404 错误日志
        if (error.code === '42P01' || error.message?.includes('does not exist') || (error as any).status === 404) return;
        throw error;
      }
      if (data) {
        addGraphicsQuestionToPaper((data as any).question_data);
        await supabase.from('temp_graphics_questions' as any).delete().eq('id', (data as any).id);
      }
    } catch (e) {
      console.error('加载导入数据失败:', e);
    }
  };

  const loadImportedPaperData = (data: any) => {
    if (data.title) {
      setTitle(data.title);
    }
    if (data.settings) {
      setPaperSettings({
        year: data.settings.year || '',
        month: data.settings.month || '',
        organization: data.settings.organization || '',
        source: data.settings.source || '',
        nature: data.settings.nature || '',
        categoryType: data.settings.categoryType || '',
        level_text: data.settings.level_text || '',
        paperType: data.settings.paperType || '',
        duration: data.settings.duration || ''
      });
    }
    if (data.questions && Array.isArray(data.questions)) {
      const importedQuestions: Question[] = data.questions.map((q: any, index: number) => ({
        id: crypto.randomUUID(),
        type: q.type || 'single_choice',
        subType: q.subType,
        title: q.title || '',
        content: q.content || '',
        placeholder: '',
        required: true,
        inputType: '文本',
        options: q.options || [],
        score: q.score || 0,
        showAnswer: false,
        titleImages: q.titleImages || [],
        titleImageWidths: q.titleImageWidths || [],
        answerImages: q.answerImages || [],
        answerImageWidths: q.answerImageWidths || [],
        codeTemplate: q.codeTemplate || '',
        codeLanguage: q.codeLanguage || '',
        allowImageUpload: q.allowImageUpload !== false,
        maxImageSize: q.maxImageSize || 5,
        allowTextInput: q.allowTextInput !== false,
        maxTextLength: q.maxTextLength || 800,
        allowFileUpload: q.allowFileUpload || false,
        maxFileSize: q.maxFileSize || 10,
        gradingMode: 'ai',
        scoringPoints: q.scoringPoints || '',
        explanation: q.explanation || '',
        answerText: q.answerText || '',
        referenceAnswer: q.answerText || '',
        scoringPointsImages: q.scoringPointsImages || [],
        scoringPointsImageWidths: q.scoringPointsImageWidths || [],
        referenceAnswerImages: q.referenceAnswerImages || [],
        referenceAnswerImageWidths: q.referenceAnswerImageWidths || [],
        scratchSubtype: q.scratchSubtype,
        scratchConfig: q.scratchConfig,
        referenceSb3Url: q.referenceSb3Url
      }));
      setQuestions(importedQuestions);
    }
  };

  const addGraphicsQuestionToPaper = (graphicsQ: any) => {
    const newQuestion: Question = {
      id: graphicsQ.id,
      type: 'scratch_coding',
      subType: 'scratch',
      title: '',  // 题目名称留空，由管理员手动输入
      content: graphicsQ.content || '',  // 解析的题干内容放到 content 字段（富文本编辑器）
      placeholder: '请输入代码',
      required: true,
      inputType: '文本',
      options: [],
      score: graphicsQ.score || 15,
      showAnswer: false,
      titleImages: [],
      titleImageWidths: [],
      imagePosition: 'below',
      answerImages: [],
      answerImageWidths: [],
      codeTemplate: '',
      codeLanguage: 'scratch',
      allowImageUpload: true,
      maxImageSize: 5,
      allowTextInput: false,
      maxTextLength: 0,
      allowFileUpload: false,
      maxFileSize: 0,
      gradingMode: 'ai',
      scoringPoints: graphicsQ.answerItems?.map((item: any) => item.name).join('、') || '',
      explanation: '',  // 不再使用 explanation 存储题干
      answerText: '',
      scoringPointsImages: [],
      scoringPointsImageWidths: [],
      referenceAnswerImages: [],
      referenceAnswerImageWidths: [],
      scratchAnswerItems: graphicsQ.answerItems?.map((item: any, idx: number) => ({
        id: item.id || String(idx + 1),
        name: item.name || `作答项${idx + 1}`,
        placeholder: item.placeholder || '请上传程序截图',
        required: true,
        maxSizeMB: 5
      })) || [{ id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }],
      scratchSubtype: graphicsQ.scratchSubtype || 'operation',
      scratchConfig: graphicsQ.scratchConfig
    };
    setQuestions(sortQuestionsByType([...questions, newQuestion]));
    // 导航到新题目位置
    const sortedQuestions = sortQuestionsByType([...questions, newQuestion]);
    const newIndex = sortedQuestions.findIndex(q => q.id === newQuestion.id);
    if (newIndex >= 0) setCurrentIndex(newIndex);
  };

  const loadPaperForEdit = async (paperId: string) => {
    try {
      const { data: paper, error } = await supabase
        .from(papersTable)
        .select('*')
        .eq('id', paperId)
        .single();

      if (error || !paper) {
        alert('加载试卷失败');
        return;
      }

      const { data: questionsData, error: questionsError } = await supabase
        .from(questionsTable)
        .select('*')
        .eq('paper_id', paperId)
        .in('status', ['published', 'draft', 'hidden'])
        .order('order_index', { ascending: true });

      if (questionsError) {
        console.error('加载题目失败:', questionsError);
      }

      setTitle(paper.title || '');
      setDescription(paper.description || '');
      setRemark(paper.remark || '');
      setPaperSettings({
        year: String(paper.year || ''),
        month: paper.month ? String(paper.month) : '',
        organization: paper.organization || '',
        source: paper.paper_type === 'real' ? '真题' : paper.paper_type === 'mock' ? '模拟题' : '',
        nature: paper.is_free !== false ? '免费' : '付费',
        categoryType: categoryStore.getCategoryNameByCode(paper.category_type) || paper.category_type || '',
        level_text: paper.level_text || '',
        paperType: '',
        duration: paper.duration ? String(paper.duration) : ''
      });
      setSelectedCategory(paper.category_id || '');
      setIsPendingPaper(false);

      if (questionsData && questionsData.length > 0) {
        const convertedQuestions: Question[] = questionsData.map((q: any, index: number) => {
          const typeMap: Record<string, string> = {
            'single_choice': 'single_choice',
            'multiple_choice': 'multi_choice',
            'judge': 'judge',
            'subjective': 'subjective',
            'python': 'python_coding',
            'cpp': 'cpp_coding',
            'graphic': 'scratch_coding',
            'graphics': 'scratch_coding',
            'python_coding': 'python_coding',
            'cpp_coding': 'cpp_coding',
            'scratch_coding': 'scratch_coding',
            'coding': 'python_coding'
          };

          const mappedType = typeMap[q.question_type] || 'single_choice';
          const isScratchCoding = mappedType === 'scratch_coding';

          const baseQuestion: Question = {
            id: q.id || Date.now().toString() + index,
            type: mappedType,
            subType: isScratchCoding ? 'scratch' : undefined,
            title: q.title ?? '', // 题目名称（新字段，从数据库 title 字段）
            content: (() => { // 题干内容（数据库 content 字段，富文本）
              let titleContent = q.content || '';
              const widths = q.title_image_widths;
              const images = q.title_images;

              // 如果 content 不包含 <img> 标签（纯文本），但 title_images 数组有图片，
              // 则将图片 URL 合并到 content 中，生成包含 <img> 标签的 HTML
              if (!titleContent.includes('<img') && images && images.length > 0) {
                // 将纯文本转为 HTML <p> 标签
                const textHtml = titleContent
                  .split('\n')
                  .map(line => line.trim() ? `<p>${line}</p>` : '<p></p>')
                  .join('');
                // 在文本后追加图片
                const imagesHtml = images.map((imgUrl: string, idx: number) => {
                  const width = (widths && widths[idx]) || 100;
                  return `<img src="${imgUrl}" style="width:${width}%;">`;
                }).join('');
                titleContent = textHtml + imagesHtml;
              }

              // 如果 content 已包含 <img> 标签，注入宽度样式
              if (titleContent.includes('<img') && widths && widths.length > 0) {
                let imgIndex = 0;
                titleContent = titleContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
                  const width = widths[imgIndex] || 100;
                  imgIndex++;
                  if (attrs.includes('style="')) {
                    return `<img${attrs.replace(/style="([^"]*)"/, (m, s) => {
                      let newStyle = s.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                      return `style="width:${width}%;${newStyle}"`;
                    })}>`;
                  }
                  return `<img${attrs} style="width:${width}%;">`;
                });
              }
              return titleContent;
            })(),
            placeholder: isScratchCoding ? '请输入代码' : '',
            required: true,
            inputType: isScratchCoding ? '文本' : '文本',
            options: (q.options || []).map((opt: any, optIndex: number) => ({
              id: opt.id || String.fromCharCode(65 + optIndex),
              label: opt.label || opt.id || String.fromCharCode(65 + optIndex),
              text: opt.text || '',
              isCorrect: opt.isCorrect !== undefined ? opt.isCorrect : (q.correct_answer ? q.correct_answer.includes(opt.id) || q.correct_answer.includes(opt.label) : false),
              // 兼容旧字段
              image: opt.image || opt.image_url || '',
              imageWidth: (opt.imageWidth || opt.image_width || 100) === 50 ? 100 : (opt.imageWidth || opt.image_width || 100),
              // 新字段：多张图片支持
              images: opt.images || (opt.image ? [opt.image] : []),
              imageWidths: opt.imageWidths || (opt.imageWidth ? [opt.imageWidth] : [])
            })),
            score: q.score || (isScratchCoding ? 15 : 2),
            showAnswer: false,
            titleImages: (q.title_images as string[]) || [],
            titleImageWidths: q.title_image_widths || q.title_images?.map(() => 100) || [],
            imagePosition: q.image_position || 'below',
            titleImageLayout: q.title_image_layout || 'auto',
            optionLayout: q.option_layout || 'grid',
            optionImageWidths: q.option_image_widths || {},
            answerImages: [],
            answerImageWidths: [],
            codeTemplate: '',
            codeLanguage: isScratchCoding ? 'scratch' : '',
            allowImageUpload: isScratchCoding,
            maxImageSize: 5,
            allowTextInput: !isScratchCoding,
            maxTextLength: isScratchCoding ? 0 : 800,
            allowFileUpload: false,
            maxFileSize: 10,
            gradingMode: 'ai',
            scoringPoints: (() => {
              let scoringContent = q.scoring_points || '';
              // 清除残留的 [IMG:xxx] 占位符（后端已替换为实际图片，但旧数据可能仍有占位符）
              scoringContent = scoringContent.replace(/\[IMG:[^\]]+\]/g, '');
              const scoringWidths = q.scoring_points_image_widths;
              // 过滤无效的图片URL（只保留以 http 或 /storage/ 开头的有效URL）
              const scoringImages = (q.scoring_points_images as string[] || []).filter(
                url => url && (url.startsWith('http') || url.startsWith('/storage/'))
              );
              if (!scoringContent.includes('<img') && scoringImages && scoringImages.length > 0) {
                const textHtml = scoringContent
                  .split('\n')
                  .map(line => line.trim() ? `<p>${line}</p>` : '<p></p>')
                  .join('');
                const imagesHtml = scoringImages.map((imgUrl: string, idx: number) => {
                  const width = (scoringWidths && scoringWidths[idx]) || 100;
                  return `<img src="${imgUrl}" style="width:${width}%;">`;
                }).join('');
                scoringContent = textHtml + imagesHtml;
              }
              if (scoringContent.includes('<img') && scoringWidths && scoringWidths.length > 0) {
                let imgIndex = 0;
                scoringContent = scoringContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
                  const width = scoringWidths[imgIndex] || 100;
                  imgIndex++;
                  if (attrs.includes('style="')) {
                    return `<img${attrs.replace(/style="([^"]*)"/, (m, s) => {
                      let newStyle = s.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                      return `style="width:${width}%;${newStyle}"`;
                    })}>`;
                  }
                  return `<img${attrs} style="width:${width}%;">`;
                });
              }
              return scoringContent;
            })(),
            explanation: q.explanation || '',
            answerText: (() => {
              let refContent = q.reference_answer || '';
              // 清除残留的 [IMG:xxx] 占位符
              refContent = refContent.replace(/\[IMG:[^\]]+\]/g, '');
              const refWidths = q.reference_answer_image_widths;
              // 过滤无效的图片URL
              const refImages = (q.reference_answer_images as string[] || []).filter(
                url => url && (url.startsWith('http') || url.startsWith('/storage/'))
              );
              if (!refContent.includes('<img') && refImages && refImages.length > 0) {
                const textHtml = refContent
                  .split('\n')
                  .map(line => line.trim() ? `<p>${line}</p>` : '<p></p>')
                  .join('');
                const imagesHtml = refImages.map((imgUrl: string, idx: number) => {
                  const width = (refWidths && refWidths[idx]) || 100;
                  return `<img src="${imgUrl}" style="width:${width}%;">`;
                }).join('');
                refContent = textHtml + imagesHtml;
              }
              if (refContent.includes('<img') && refWidths && refWidths.length > 0) {
                let imgIndex = 0;
                refContent = refContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
                  const width = refWidths[imgIndex] || 100;
                  imgIndex++;
                  if (attrs.includes('style="')) {
                    return `<img${attrs.replace(/style="([^"]*)"/, (m, s) => {
                      let newStyle = s.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                      return `style="width:${width}%;${newStyle}"`;
                    })}>`;
                  }
                  return `<img${attrs} style="width:${width}%;">`;
                });
              }
              return refContent;
            })(),
            referenceAnswer: (() => {
              let refContent = q.reference_answer || '';
              // 清除残留的 [IMG:xxx] 占位符
              refContent = refContent.replace(/\[IMG:[^\]]+\]/g, '');
              const refWidths = q.reference_answer_image_widths;
              // 过滤无效的图片URL
              const refImages = (q.reference_answer_images as string[] || []).filter(
                url => url && (url.startsWith('http') || url.startsWith('/storage/'))
              );
              if (!refContent.includes('<img') && refImages && refImages.length > 0) {
                const textHtml = refContent
                  .split('\n')
                  .map(line => line.trim() ? `<p>${line}</p>` : '<p></p>')
                  .join('');
                const imagesHtml = refImages.map((imgUrl: string, idx: number) => {
                  const width = (refWidths && refWidths[idx]) || 100;
                  return `<img src="${imgUrl}" style="width:${width}%;">`;
                }).join('');
                refContent = textHtml + imagesHtml;
              }
              if (refContent.includes('<img') && refWidths && refWidths.length > 0) {
                let imgIndex = 0;
                refContent = refContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
                  const width = refWidths[imgIndex] || 100;
                  imgIndex++;
                  if (attrs.includes('style="')) {
                    return `<img${attrs.replace(/style="([^"]*)"/, (m, s) => {
                      let newStyle = s.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                      return `style="width:${width}%;${newStyle}"`;
                    })}>`;
                  }
                  return `<img${attrs} style="width:${width}%;">`;
                });
              }
              return refContent;
            })(),
            scoringPointsImages: (q.scoring_points_images as string[]) || [],
            scoringPointsImageWidths: q.scoring_points_image_widths || [],
            referenceAnswerImages: (q.reference_answer_images as string[]) || [],
            referenceAnswerImageWidths: q.reference_answer_image_widths || [],
            scratchAnswerItems: isScratchCoding ? [{ id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }] : undefined,
            scratchSubtype: q.scratch_subtype as ScratchQuestionSubtype,
            scratchConfig: q.scratch_config,
            referenceSb3Url: q.reference_sb3_url
          };

          if (baseQuestion.type === 'single_choice' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '', isCorrect: true },
              { id: 'B', label: 'B', text: '', isCorrect: false },
              { id: 'C', label: 'C', text: '', isCorrect: false },
              { id: 'D', label: 'D', text: '', isCorrect: false }
            ];
          } else if (baseQuestion.type === 'judge' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '正确', isCorrect: true },
              { id: 'B', label: 'B', text: '错误', isCorrect: false }
            ];
          }

          return baseQuestion;
        });

        setQuestions(sortQuestionsByType(convertedQuestions));

        // 如果有目标题目ID，自动定位到该题目
        if (targetQuestionId) {
          setTimeout(() => {
            if (unmountedRef.current) return;
            try {
              const questionElement = document.getElementById(`question-${targetQuestionId}`);
              if (questionElement && questionElement.isConnected) {
                questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // 高亮显示该题目
                questionElement.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
                setTimeout(() => {
                  if (unmountedRef.current) return;
                  try {
                    if (questionElement.isConnected) {
                      questionElement.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
                    }
                  } catch (_) { /* 组件已卸载，静默忽略 */ }
                }, 3000);
              }
            } catch (_) { /* 组件已卸载，静默忽略 */ }
          }, 500);
        }
      } else {
        setQuestions([]);
      }
    } catch (err) {
      alert('加载试卷异常');
    }
  };

  const loadPaperDraftData = async (draftId: string) => {
    try {
      const { data: draft, error: draftError } = await supabase
        .from('paper_drafts')
        .select('*')
        .eq('id', draftId)
        .single();

      if (draftError || !draft) {
        alert('加载草稿失败');
        return;
      }

      setTitle(draft.title || '');
      setRemark(draft.remark || '');
      setIsPendingPaper(true);

      if (draft.questions && Array.isArray(draft.questions)) {
        const convertedQuestions: Question[] = draft.questions.map((q: any, index: number) => {
          const baseQuestion: Question = {
            id: q.id || Date.now().toString() + index,
            type: q.type || 'single_choice',
            subType: q.subType,
            title: q.title || '', // 题目名称
            content: q.content || '', // 题干内容
            placeholder: q.placeholder || '请选择',
            required: true,
            inputType: q.inputType || '文本',
            options: (q.options || []).map((opt: any, optIndex: number) => ({
              id: opt.id || opt.label || String.fromCharCode(65 + optIndex),
              label: opt.label || opt.id || String.fromCharCode(65 + optIndex),
              text: opt.text || '',
              isCorrect: opt.isCorrect !== undefined ? opt.isCorrect : (q.correct_answer ? q.correct_answer.includes(opt.id) || q.correct_answer.includes(opt.label) : false),
              // 兼容旧字段
              image: opt.image || opt.image_url || '',
              imageWidth: (opt.imageWidth || opt.image_width || 100) === 50 ? 100 : (opt.imageWidth || opt.image_width || 100),
              // 新字段：多张图片支持
              images: opt.images || (opt.image ? [opt.image] : []),
              imageWidths: opt.imageWidths || (opt.imageWidth ? [opt.imageWidth] : [])
            })),
            score: q.score || 2,
            showAnswer: false,
            titleImages: q.titleImages || [],
            titleImageWidths: q.titleImageWidths || [],
            imagePosition: q.imagePosition || 'below',
            titleImageLayout: q.titleImageLayout || 'auto',
            optionLayout: q.optionLayout || 'grid',
            answerImages: q.answerImages || [],
            answerImageWidths: q.answerImageWidths || [],
            codeTemplate: q.codeTemplate || '',
            codeLanguage: q.codeLanguage || '',
            allowImageUpload: q.allowImageUpload || false,
            maxImageSize: q.maxImageSize || 5,
            allowTextInput: q.allowTextInput !== false,
            maxTextLength: q.maxTextLength || 800,
            allowFileUpload: q.allowFileUpload || false,
            maxFileSize: q.maxFileSize || 10,
            gradingMode: q.gradingMode || 'manual',
            scoringPoints: q.scoringPoints || '',
            explanation: q.explanation || '',
            answerText: q.answerText || '',
            referenceAnswer: q.answerText || '',
            scoringPointsImages: q.scoringPointsImages || [],
            scoringPointsImageWidths: q.scoringPointsImageWidths || [],
            referenceAnswerImages: q.referenceAnswerImages || [],
            referenceAnswerImageWidths: q.referenceAnswerImageWidths || [],
            scratchAnswerItems: q.scratchAnswerItems,
            scratchSubtype: q.scratchSubtype,
            scratchConfig: q.scratchConfig,
            referenceSb3Url: q.referenceSb3Url
          };

          if (baseQuestion.type === 'single_choice' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '', isCorrect: true },
              { id: 'B', label: 'B', text: '', isCorrect: false },
              { id: 'C', label: 'C', text: '', isCorrect: false },
              { id: 'D', label: 'D', text: '', isCorrect: false }
            ];
          } else if (baseQuestion.type === 'judge' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '正确', isCorrect: true },
              { id: 'B', label: 'B', text: '错误', isCorrect: false }
            ];
          }

          return baseQuestion;
        });

        setQuestions(sortQuestionsByType(convertedQuestions));
      }
    } catch (err) {
      alert('加载草稿异常');
    }
  };

  const loadPendingPaperData = async (pendingId: string) => {
    try {
      const result: any = await supabase
        .from(pendingPapersTable as any)
        .select('*')
        .eq('id', pendingId)
        .single();
      const pendingPaper = result.data;
      const paperError = result.error;

      if (paperError || !pendingPaper) {
        alert('加载待编辑试卷失败');
        return;
      }

      const { data: questionsData, error: questionsError } = await supabase
        .from(questionsTable)
        .select('*')
        .eq('pending_paper_id', pendingId)
        .order('order_index', { ascending: true });

      if (questionsError) {
        console.error('加载题目失败:', questionsError);
      }

      setTitle((pendingPaper as any).title || '');
      setRemark((pendingPaper as any).remark || '');
      setIsPendingPaper(true);

      // 设置试卷配置信息
      setPaperSettings({
        year: String((pendingPaper as any).year || ''),
        month: (pendingPaper as any).month ? String((pendingPaper as any).month) : '',
        organization: (pendingPaper as any).organization || '',
        source: (pendingPaper as any).paper_type === 'real' ? '真题' : (pendingPaper as any).paper_type === 'mock' ? '模拟题' : '',
        nature: (pendingPaper as any).is_free === true ? '免费' : '付费',
        categoryType: categoryStore.getCategoryNameByCode((pendingPaper as any).category_type) || (pendingPaper as any).category_type || '',
        level_text: (pendingPaper as any).level_text || '',
        paperType: '',
        duration: (pendingPaper as any).duration ? String((pendingPaper as any).duration) : ''
      });

      if (questionsData && questionsData.length > 0) {
        const convertedQuestions: Question[] = questionsData.map((q: any, index: number) => {
          const typeMap: Record<string, string> = {
            'single_choice': 'single_choice',
            'multiple_choice': 'multi_choice',
            'judge': 'judge',
            'subjective': 'subjective',
            'python': 'python_coding',
            'cpp': 'cpp_coding',
            'graphic': 'scratch_coding',
            'graphics': 'scratch_coding',
            'python_coding': 'python_coding',
            'cpp_coding': 'cpp_coding',
            'scratch_coding': 'scratch_coding',
            'coding': 'python_coding'
          };

          const mappedType = typeMap[q.question_type] || 'single_choice';
          const isScratchCoding = mappedType === 'scratch_coding';

          const baseQuestion: Question = {
            id: q.id || Date.now().toString() + index,
            type: mappedType,
            subType: isScratchCoding ? 'scratch' : undefined,
            title: q.title ?? '', // 题目名称（新字段，从数据库 title 字段）
            content: (() => { // 题干内容（数据库 content 字段，富文本）
              let titleContent = q.content || '';
              const widths = q.title_image_widths;
              const images = q.title_images;

              // 如果 content 不包含 <img> 标签（纯文本），但 title_images 数组有图片，
              // 则将图片 URL 合并到 content 中，生成包含 <img> 标签的 HTML
              if (!titleContent.includes('<img') && images && images.length > 0) {
                // 将纯文本转为 HTML <p> 标签
                const textHtml = titleContent
                  .split('\n')
                  .map(line => line.trim() ? `<p>${line}</p>` : '<p></p>')
                  .join('');
                // 在文本后追加图片
                const imagesHtml = images.map((imgUrl: string, idx: number) => {
                  const width = (widths && widths[idx]) || 100;
                  return `<img src="${imgUrl}" style="width:${width}%;">`;
                }).join('');
                titleContent = textHtml + imagesHtml;
              }

              // 如果 content 已包含 <img> 标签，注入宽度样式
              if (titleContent.includes('<img') && widths && widths.length > 0) {
                let imgIndex = 0;
                titleContent = titleContent.replace(/<img([^>]*?)>/g, (match, attrs) => {
                  const width = widths[imgIndex] || 100;
                  imgIndex++;
                  if (attrs.includes('style="')) {
                    return `<img${attrs.replace(/style="([^"]*)"/, (m, s) => {
                      let newStyle = s.replace(/(?<!max-)width:\s*\d+%;?\s*/g, '');
                      return `style="width:${width}%;${newStyle}"`;
                    })}>`;
                  }
                  return `<img${attrs} style="width:${width}%;">`;
                });
              }
              return titleContent;
            })(),
            placeholder: isScratchCoding ? '请输入代码' : '',
            required: true,
            inputType: isScratchCoding ? '文本' : '文本',
            options: (q.options || []).map((opt: any, optIndex: number) => ({
              id: opt.id || String.fromCharCode(65 + optIndex),
              label: opt.label || opt.id || String.fromCharCode(65 + optIndex),
              text: opt.text || '',
              isCorrect: opt.isCorrect !== undefined ? opt.isCorrect : (q.correct_answer ? q.correct_answer.includes(opt.id) || q.correct_answer.includes(opt.label) : false),
              // 兼容旧字段
              image: opt.image || opt.image_url || '',
              imageWidth: (opt.imageWidth || opt.image_width || 100) === 50 ? 100 : (opt.imageWidth || opt.image_width || 100),
              // 新字段：多张图片支持
              images: opt.images || (opt.image ? [opt.image] : []),
              imageWidths: opt.imageWidths || (opt.imageWidth ? [opt.imageWidth] : [])
            })),
            score: q.score || (isScratchCoding ? 15 : 2),
            showAnswer: false,
            titleImages: (q.title_images as string[]) || [],
            titleImageWidths: q.title_image_widths || q.title_images?.map(() => 100) || [],
            imagePosition: q.image_position || 'below',
            titleImageLayout: q.title_image_layout || 'auto',
            optionLayout: q.option_layout || 'grid',
            optionImageWidths: q.option_image_widths || {},
            answerImages: [],
            answerImageWidths: [],
            codeTemplate: '',
            codeLanguage: isScratchCoding ? 'scratch' : '',
            allowImageUpload: isScratchCoding,
            maxImageSize: 5,
            allowTextInput: !isScratchCoding,
            maxTextLength: isScratchCoding ? 0 : 800,
            allowFileUpload: false,
            maxFileSize: 10,
            gradingMode: 'ai',
            scoringPoints: '',
            explanation: q.explanation || '',
            answerText: '',
            scoringPointsImages: (q.scoring_points_images as string[]) || [],
            scoringPointsImageWidths: q.scoring_points_image_widths || [],
            referenceAnswerImages: (q.reference_answer_images as string[]) || [],
            referenceAnswerImageWidths: q.reference_answer_image_widths || [],
            scratchAnswerItems: isScratchCoding ? [{ id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }] : undefined,
            scratchSubtype: q.scratch_subtype as ScratchQuestionSubtype,
            scratchConfig: q.scratch_config,
            referenceSb3Url: q.reference_sb3_url
          };

          if (baseQuestion.type === 'single_choice' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '', isCorrect: true },
              { id: 'B', label: 'B', text: '', isCorrect: false },
              { id: 'C', label: 'C', text: '', isCorrect: false },
              { id: 'D', label: 'D', text: '', isCorrect: false }
            ];
          } else if (baseQuestion.type === 'judge' && baseQuestion.options.length === 0) {
            baseQuestion.options = [
              { id: 'A', label: 'A', text: '正确', isCorrect: true },
              { id: 'B', label: 'B', text: '错误', isCorrect: false }
            ];
          }

          return baseQuestion;
        });

        setQuestions(sortQuestionsByType(convertedQuestions));
      }
    } catch (err) {
      alert('加载待编辑试卷异常');
    }
  };

  const publishPendingPaper = async () => {
    if (isSaving) return;
    if (!isAllSettingsSelected()) {
      alert('请完整填写所有试卷设置信息（年份、月份、考评体系、课程分类、级别分类、试卷类型、免费/付费、考试时长）');
      return;
    }

    if (!pendingPaperId) {
      alert('待编辑试卷ID不存在');
      return;
    }

    // 教师模式下跳过级别验证
    if (!actualIsTeacherMode) {
      const selectedLevelName = paperSettings.level_text;
      const validLevelNames = levels.map(l => l.name);
      if (!validLevelNames.includes(selectedLevelName)) {
        alert(`级别分类"${selectedLevelName}"不存在于 exam_levels 表中，请重新选择`);
        return;
      }
    }

    setIsSaving(true);
    setSavingAction('published');
    try {
      const totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
      const singleChoiceCount = questions.filter(q => q.type === 'single_choice').length;
      const multiChoiceCount = questions.filter(q => q.type === 'multi_choice').length;
      const judgeCount = questions.filter(q => q.type === 'judge').length;
      const codingCount = questions.filter(q => ['python_coding', 'cpp_coding', 'scratch_coding', 'coding', 'subjective'].includes(q.type)).length;
      const newPaperId = generateUUID();

      const autoTitle = generateAutoTitle();
      const fullTitle = title || autoTitle;

      const { data: { session } } = await supabase.auth.getSession();
      const adminId = session?.user?.id || 'admin';
      const adminName = '管理员';
      const adminUsername = 'admin';
      const currentTime = new Date().toISOString();

      const paperData = {
        id: newPaperId,
        title: fullTitle,
        description,
        year: paperSettings.year,
        month: paperSettings.month,
        organization: paperSettings.organization,
        source: paperSettings.source,
        nature: paperSettings.nature,
        total_score: totalScore,
        question_count: questions.filter(q => q.type !== 'paragraph').length,
        questions: questions.map(q => {
          const baseQuestion = {
            id: q.id,
            type: q.type,
            subType: q.subType,
            questionStem: q.content || q.title,
            placeholder: q.placeholder,
            required: q.required,
            inputType: q.inputType,
            options: q.options,
            score: q.score,
            showAnswer: q.showAnswer,
            titleImages: q.titleImages,
            titleImageWidths: q.titleImageWidths,
            answerImages: q.answerImages,
            answerImageWidths: q.answerImageWidths,
            codeTemplate: q.codeTemplate,
            codeLanguage: q.codeLanguage,
            allowImageUpload: q.allowImageUpload,
            maxImageSize: q.maxImageSize,
            allowTextInput: q.allowTextInput,
            maxTextLength: q.maxTextLength,
            allowFileUpload: q.allowFileUpload,
            maxFileSize: q.maxFileSize,
            gradingMode: q.gradingMode,
            scoringPoints: q.scoringPoints,
            scoringPointsImages: q.scoringPointsImages,
            scoringPointsImageWidths: q.scoringPointsImageWidths,
            explanation: q.explanation,
            answerText: q.answerText,
            answerAnalysis: q.answerAnalysis,
            referenceAnswer: q.answerText,
            referenceAnswerImages: q.referenceAnswerImages,
            referenceAnswerImageWidths: q.referenceAnswerImageWidths,
            scratchSubtype: q.scratchSubtype,
            scratchConfig: q.scratchConfig,
            referenceSb3Url: q.referenceSb3Url
          };
          if (q.type === 'scratch_coding') {
            return {
              ...baseQuestion,
              scratchAnswerItems: q.scratchAnswerItems
            };
          }
          return baseQuestion;
        }),
        category_id: selectedCategory || null,
        category_type: paperSettings.categoryType || null,
        level_text: paperSettings.level_text,
        paper_type: paperSettings.source === '真题' ? 'real' : paperSettings.source === '模拟题' ? 'mock' : 'real',
        is_published: true,
        is_free: paperSettings.nature === '免费',
        remark: remark || '',
        duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
        status: 'published',
        created_at: currentTime,
        updated_at: currentTime,
        created_by: adminName,
        edit_history: [{ time: currentTime, user: adminUsername, type: '创建' }],
        single_choice_count: singleChoiceCount,
        multi_choice_count: multiChoiceCount,
        judge_count: judgeCount,
        coding_count: codingCount
      };

      const result = await paperStorage.savePaper(paperData);
      if (!result.success) {
        alert(result.message || '保存失败');
        return;
      }

      await supabase
        .from(pendingPapersTable as any)
        .update({ status: 'published' })
        .eq('id', pendingPaperId as any);

      await supabase
        .from(questionsTable)
        .update({ status: 'published' })
        .eq('pending_paper_id', pendingPaperId);

      alert('发布成功！试卷已保存到试卷列表');
      navigate(actualIsTeacherMode ? '/teacher/papers' : '/admin/papers');
    } catch (err) {
      alert('发布失败，请稍后重试');
    } finally {
      setIsSaving(false);
      setSavingAction(null);
    }
  };

  const fetchCategories = async () => {
    const { data: catsData } = await supabase.from('course_categories').select('*');
    const cats = (catsData || []).map((cat: any) => ({ id: String(cat.id), name: cat.name }));
    setCategories(cats);
  };

  useEffect(() => {
    const loadCategories = async () => {
      await categoryStore.init();
      setOrgs(categoryStore.getOrganizations());
      setCats(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    };
    loadCategories();

    const unsubscribe = categoryStore.subscribe(() => {
      setOrgs(categoryStore.getOrganizations());
      setCats(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    });
    return unsubscribe;
  }, []);

  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const isAllSettingsSelected = () => {
    if (actualIsTeacherMode) {
      // 教师模式只需要有标题即可
      return !!title?.trim();
    }
    return paperSettings.year && paperSettings.organization && paperSettings.categoryType &&
           paperSettings.level_text && paperSettings.source && paperSettings.nature && paperSettings.duration;
  };

  const savePaper = async (status: 'draft' | 'published' | 'saved') => {
    if (isSaving) return;
    if (!isAllSettingsSelected()) {
      alert('请完整填写所有试卷设置信息（年份、月份、考评体系、课程分类、级别分类、试卷类型、免费/付费、考试时长）');
      return;
    }

    // 教师模式下跳过级别验证
    if (!actualIsTeacherMode) {
      const selectedLevelName = paperSettings.level_text;
      const validLevelNames = levels.map(l => l.name);
      if (!validLevelNames.includes(selectedLevelName)) {
        alert(`级别分类"${selectedLevelName}"不存在于 exam_levels 表中，请重新选择`);
        return;
      }
    }

    setIsSaving(true);
    setSavingAction(status);
    const totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
    const singleChoiceCount = questions.filter(q => q.type === 'single_choice').length;
    const multiChoiceCount = questions.filter(q => q.type === 'multi_choice').length;
    const judgeCount = questions.filter(q => q.type === 'judge').length;
    const codingCount = questions.filter(q => ['python_coding', 'cpp_coding', 'scratch_coding', 'coding', 'subjective'].includes(q.type)).length;
    const paperId = editPaperId || generateUUID();

    const autoTitle = generateAutoTitle();
    const fullTitle = title?.trim() || autoTitle || '未命名试卷';

    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user?.id || 'admin';
    const adminName = '管理员';
    const currentTime = new Date().toISOString();

    try {
      if (isPendingPaper && pendingPaperId && status !== 'draft') {
        await supabase.from(pendingPapersTable as any).delete().eq('id', pendingPaperId as any);
        await supabase.from(questionsTable).delete().eq('pending_paper_id', pendingPaperId);
      }

      if (editPaperId && status === 'draft') {
        await supabase.from(papersTable).delete().eq('id', editPaperId);
        await supabase.from(questionsTable).delete().eq('paper_id', editPaperId);
      }

      const paperData = {
        id: paperId,
        title: fullTitle,
        description,
        year: paperSettings.year ? parseInt(paperSettings.year) : null,
        month: paperSettings.month ? parseInt(paperSettings.month) : null,
        organization: paperSettings.organization,
        category_type: paperSettings.categoryType || null,
        level_text: paperSettings.level_text,
        paper_type: paperSettings.source === '真题' ? 'real' : paperSettings.source === '模拟题' ? 'mock' : 'real',
        duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
        is_free: paperSettings.nature === '免费',
        is_published: status === 'published', // 仅"保存并发布"才发布到刷题平台
        status: status === 'saved' ? 'published' : status, // "保存为试卷"映射为 published（数据库约束只允许 draft/published/archived）
        total_score: totalScore,
        question_count: questions.length,
        single_choice_count: singleChoiceCount,
        multi_choice_count: multiChoiceCount,
        judge_count: judgeCount,
        coding_count: codingCount,
        remark: remark || '',
        created_at: currentTime,
        updated_at: currentTime
      };

      // 清理 NaN 和 undefined 值，避免 Supabase 400 错误
      Object.keys(paperData).forEach(key => {
        const val = (paperData as any)[key];
        if (Number.isNaN(val) || val === undefined) {
          (paperData as any)[key] = null;
        }
      });

      if (editPaperId && status !== 'draft') {
        // update 时不能包含 id 字段，否则 Supabase 会返回 400
        const { id: _, ...updateData } = paperData;
        const { data: updateResult, error } = await supabase.from(papersTable).update(updateData).eq('id', paperId);
        if (error) {
          console.error('Supabase 更新错误详情:', error);
          throw error;
        }
        await supabase.from(questionsTable).delete().eq('paper_id', paperId);
      } else {
        const { error } = await supabase.from(papersTable).insert(paperData);
        if (error) throw error;
      }

      // 批量构建所有题目数据，一次性插入，避免循环单条 insert 中途网络失败导致数据不一致
      const questionsToInsert: any[] = [];
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];

        // 优先使用 content（富文本题干），回退到 title（短名称）
        let processedContent = q.content || q.title || '';

        // 关键修复：title_images / title_image_widths 必须从最新的 content 中提取
        // 否则用户在富文本编辑器中删除图片后，content 已干净但 title_images 仍保留旧 URL，
        // 重新加载时（见加载逻辑）会把已失效的 URL 拼回 content，导致破碎图标复现
        const extractedTitleImages = extractImagesFromHtml(processedContent);
        let processedTitleImages = extractedTitleImages.urls;
        let processedTitleImageWidths = extractedTitleImages.widths;

        // 参考答案：优先使用 referenceAnswer（Scratch编辑器更新的字段），回退到 answerText
        const refAnswer = q.referenceAnswer || q.answerText || '';
        questionsToInsert.push({
          paper_id: paperId,
          title: q.title || '', // 题目名称
          content: processedContent,
          question_type: q.type,
          score: q.score,
          order_index: i + 1,
          options: (q.options || []) as any,
          correct_answer: q.options?.filter((o: any) => o.isCorrect).map((o: any) => o.id).join(','),
          explanation: q.explanation || '',
          reference_answer: refAnswer,
          reference_answer_images: extractImagesFromHtml(refAnswer).urls,
          reference_answer_image_widths: extractImagesFromHtml(refAnswer).widths,
          scoring_points: q.scoringPoints || '',
          scoring_points_images: extractImagesFromHtml(q.scoringPoints || '').urls,
          scoring_points_image_widths: extractImagesFromHtml(q.scoringPoints || '').widths,
          title_images: processedTitleImages,
          title_image_widths: processedTitleImageWidths,
          option_image_widths: q.optionImageWidths || {},
          title_image_layout: q.titleImageLayout || 'auto',
          option_layout: q.optionLayout || 'grid',
          image_position: q.imagePosition || 'below',
          status: status === 'saved' ? 'published' : status, // "保存为试卷"映射为 published
          created_at: currentTime,
          updated_at: currentTime,
          scratch_subtype: q.scratchSubtype,
          scratch_config: q.scratchConfig,
          reference_sb3_url: q.referenceSb3Url
        });
      }

      // 一次性批量插入所有题目（单次网络请求，降低失败概率）
      if (questionsToInsert.length > 0) {
        const { error: batchQError } = await supabase.from(questionsTable).insert(questionsToInsert);
        if (batchQError) {
          // 批量插入失败：回滚 papers 表，避免出现"试卷元数据存在但题目缺失"的数据不一致
          console.error('批量插入题目失败，开始回滚 papers 表:', batchQError);
          if (editPaperId && status !== 'draft') {
            // 编辑场景：papers 表是 update 的，无法简单回滚，至少把状态改回 draft 避免误用
            await supabase.from(papersTable).update({
              status: 'draft',
              is_published: false,
              updated_at: new Date().toISOString(),
            }).eq('id', paperId);
          } else {
            // 新建场景：直接删除刚插入的 papers 记录
            await supabase.from(papersTable).delete().eq('id', paperId);
          }
          throw batchQError;
        }
      }

      // 先重置 isSaving，让按钮恢复正常状态
      setIsSaving(false);
      setSavingAction(null);
      setHasOrderChanges(false);
      // 使用 setTimeout 确保 UI 更新后再跳转
      setTimeout(() => {
        if (status === 'draft') {
          // 暂存为草稿 → 跳转到草稿列表
          if (actualIsTeacherMode) {
            navigate('/teacher/papers');
          } else {
            navigate('/admin/pending-papers');
          }
        } else {
          // 保存为试卷 / 保存并发布 → 跳转到试卷列表
          if (actualIsTeacherMode) {
            navigate('/teacher/papers');
          } else {
            navigate('/admin/papers');
          }
        }
      }, 100);
    } catch (err: any) {

      setIsSaving(false);
      setSavingAction(null);
      alert('保存失败: ' + (err?.message || '未知错误'));
    }
  };

  // 判断是否为有效的 UUID（数据库中的真实 ID）
  const isUUID = (id: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  const handleSaveQuestionLayout = async (questionId: string, layoutData: {
    titleImageWidths?: number[];
    titleImageLayout?: string;
    imagePosition?: string;
    optionImageWidths?: Record<string, number>;
    optionLayout?: string;
  }) => {
    try {
      // 临时时间戳 ID（新添加但未入库的题目）跳过 PATCH，布局数据会在保存试卷时一起写入
      if (!isUUID(questionId)) {
        // 仅更新本地状态
        setQuestions(prev => prev.map(q =>
          q.id === questionId ? {
            ...q,
            titleImageWidths: layoutData.titleImageWidths,
            titleImageLayout: layoutData.titleImageLayout,
            imagePosition: layoutData.imagePosition as 'above' | 'below',
            optionImageWidths: layoutData.optionImageWidths,
            optionLayout: layoutData.optionLayout,
          } : q
        ));
        return;
      }

      const { error } = await supabase
        .from(questionsTable)
        .update({
          title_image_widths: layoutData.titleImageWidths || [],
          title_image_layout: layoutData.titleImageLayout || 'auto',
          image_position: layoutData.imagePosition || 'below',
          option_image_widths: layoutData.optionImageWidths || {},
          option_layout: layoutData.optionLayout || 'grid',
          updated_at: new Date().toISOString(),
        })
        .eq('id', questionId);

      if (error) throw error;

      // 更新本地 questions 状态
      setQuestions(prev => prev.map(q =>
        q.id === questionId ? {
          ...q,
          titleImageWidths: layoutData.titleImageWidths,
          titleImageLayout: layoutData.titleImageLayout,
          imagePosition: layoutData.imagePosition as 'above' | 'below',
          optionImageWidths: layoutData.optionImageWidths,
          optionLayout: layoutData.optionLayout,
        } : q
      ));
    } catch (err: any) {
      console.error('保存题目布局失败:', err);
      alert('保存失败: ' + (err?.message || '未知错误'));
      throw err;
    }
  };

  // 保存单个题目的名称到数据库（防抖自动保存）
  const handleSaveQuestionTitle = useCallback(async (questionId: string, titleName: string) => {
    // 清除之前的定时器
    if (titleSaveTimerRef.current) {
      clearTimeout(titleSaveTimerRef.current);
    }

    // 设置新的防抖定时器（500ms 后保存）
    titleSaveTimerRef.current = setTimeout(async () => {
      try {
        // 临时时间戳 ID（新添加但未入库的题目）跳过数据库保存
        if (!isUUID(questionId)) {
          return;
        }

        const { error } = await supabase
          .from(questionsTable)
          .update({
            title: titleName,
            updated_at: new Date().toISOString(),
          })
          .eq('id', questionId);

        if (error) {
          console.error('保存题目名称失败:', error);
        }
      } catch (err: any) {
        console.error('保存题目名称异常:', err);
      }
    }, 500);
  }, []);

  const publishPaper = () => savePaper('published');

  const saveAsDraft = async () => {
    if (isSaving) return;
    if (paperSettings.level_text) {
      const selectedLevelName = paperSettings.level_text;
      const validLevelNames = levels.map(l => l.name);
      if (!validLevelNames.includes(selectedLevelName)) {
        alert(`级别分类"${selectedLevelName}"不存在于 exam_levels 表中，请重新选择`);
        return;
      }
    }

    setIsSaving(true);
    setSavingAction('draft');
    const totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
    const paperId = editPaperId || generateUUID();
    const currentTime = new Date().toISOString();

    try {
      const singleChoiceCount = questions.filter(q => q.type === 'single_choice').length;
      const multiChoiceCount = questions.filter(q => q.type === 'multi_choice').length;
      const judgeCount = questions.filter(q => q.type === 'judge').length;
      const codingCount = questions.filter(q => ['python_coding', 'cpp_coding', 'scratch_coding', 'coding', 'subjective'].includes(q.type)).length;

      const autoTitle = generateAutoTitle();
      const finalTitle = title?.trim() || autoTitle || '未命名试卷';

      const paperData: any = {
        id: paperId,
        title: finalTitle,
        description: description || '',
        year: paperSettings.year ? parseInt(paperSettings.year) : null,
        month: paperSettings.month ? parseInt(paperSettings.month) : null,
        organization: paperSettings.organization || '',
        category_type: paperSettings.categoryType || null,
        level_text: paperSettings.level_text || '',
        paper_type: paperSettings.source === '真题' ? 'real' : paperSettings.source === '模拟题' ? 'mock' : 'real',
        duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
        is_free: paperSettings.nature === '免费',
        is_published: false,
        status: 'draft',
        question_count: questions.length,
        single_choice_count: singleChoiceCount,
        multi_choice_count: multiChoiceCount,
        judge_count: judgeCount,
        coding_count: codingCount,
        total_score: totalScore,
        updated_at: currentTime,
        remark: remark || ''
      };

      if (editPaperId) {
        const { error } = await supabase.from(papersTable).update(paperData).eq('id', paperId);
        if (error) throw error;
        await supabase.from(questionsTable).delete().eq('paper_id', paperId);
      } else {
        paperData.created_at = currentTime;
        paperData.remark = remark || '';
        const { error } = await supabase.from(papersTable).insert(paperData);
        if (error) throw error;
      }

      // 批量构建所有题目数据，一次性插入，避免循环单条 insert 中途网络失败导致数据不一致
      const draftQuestionsToInsert: any[] = [];
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];

        // 优先使用 content（富文本题干），回退到 title（短名称）
        let processedContent = q.content || q.title || '';

        // 关键修复：title_images / title_image_widths 必须从最新的 content 中提取
        // 否则用户在富文本编辑器中删除图片后，content 已干净但 title_images 仍保留旧 URL，
        // 重新加载时（见加载逻辑）会把已失效的 URL 拼回 content，导致破碎图标复现
        const extractedTitleImages = extractImagesFromHtml(processedContent);
        let processedTitleImages = extractedTitleImages.urls;
        let processedTitleImageWidths = extractedTitleImages.widths;

        draftQuestionsToInsert.push({
          paper_id: paperId,
          title: q.title || '', // 题目名称
          content: processedContent,
          question_type: q.type,
          score: q.score,
          order_index: i + 1,
          options: q.options || [],
          correct_answer: q.options?.filter((o: any) => o.isCorrect).map((o: any) => o.id).join(','),
          explanation: q.explanation || '',
          reference_answer: q.answerText || '',
          scoring_points: q.scoringPoints || '',
          title_images: processedTitleImages,
          title_image_widths: processedTitleImageWidths,
          option_image_widths: q.optionImageWidths || {},
          title_image_layout: q.titleImageLayout || 'auto',
          option_layout: q.optionLayout || 'grid',
          image_position: q.imagePosition || 'below',
          status: 'draft',
          created_at: currentTime,
          updated_at: currentTime,
          scratch_subtype: q.scratchSubtype,
          scratch_config: q.scratchConfig,
          reference_sb3_url: q.referenceSb3Url
        });
      }

      // 一次性批量插入所有题目（单次网络请求，降低失败概率）
      if (draftQuestionsToInsert.length > 0) {
        const { error: batchQError } = await supabase.from(questionsTable).insert(draftQuestionsToInsert);
        if (batchQError) {
          // 批量插入失败：回滚 papers 表，避免出现"试卷元数据存在但题目缺失"的数据不一致
          console.error('草稿批量插入题目失败，开始回滚 papers 表:', batchQError);
          if (editPaperId) {
            // 编辑场景：papers 表是 update 的，至少把状态改回 draft 避免误用
            await supabase.from(papersTable).update({
              status: 'draft',
              is_published: false,
              updated_at: new Date().toISOString(),
            }).eq('id', paperId);
          } else {
            // 新建场景：直接删除刚插入的 papers 记录
            await supabase.from(papersTable).delete().eq('id', paperId);
          }
          throw batchQError;
        }
      }

      alert(editPaperId ? '试卷已转为草稿' : '已暂存为草稿');
      if (actualIsTeacherMode) {
        navigate('/teacher/papers');
      } else {
        navigate('/admin/pending-papers');
      }
    } catch (err: any) {
      
      alert('暂存草稿失败: ' + (err?.message || err?.error_description || '未知错误'));
    } finally {
      setIsSaving(false);
      setSavingAction(null);
    }
  };

  const openImageViewer = (imageUrl: string, title: string, currentIndex: number = 0, totalCount: number = 1) => {
    setImageViewer({ isOpen: true, imageUrl, title, currentIndex, totalCount });
  };

  const closeImageViewer = () => {
    setImageViewer({ isOpen: false, imageUrl: '', title: '', currentIndex: 0, totalCount: 0 });
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeImageViewer();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const sortQuestionsByType = (qs: Question[]) => {
    const typeOrder: Record<string, number> = {
      'single_choice': 1,
      'multi_choice': 2,
      'judge': 3,
      'python_coding': 4,
      'cpp_coding': 5,
      'scratch_coding': 6,
      'subjective': 7
    };
    return [...qs].sort((a, b) => {
      const orderA = typeOrder[a.type] || 99;
      const orderB = typeOrder[b.type] || 99;
      return orderA - orderB;
    });
  };

  const addQuestion = (typeItem: any) => {
    if (typeItem.type === 'python_coding' || typeItem.type === 'cpp_coding') {
      const newQuestion: Question = {
        id: crypto.randomUUID(),
        type: typeItem.type,
        subType: typeItem.type === 'python_coding' ? 'python' : 'cpp',
        title: '',
        placeholder: typeItem.placeholder,
        required: true,
        inputType: '文本',
        options: [],
        score: 15,
        showAnswer: false,
        titleImages: [],
        titleImageWidths: [],
        answerImages: [],
        answerImageWidths: [],
        codeTemplate: '',
        codeLanguage: typeItem.type === 'python_coding' ? 'python' : 'cpp',
        allowImageUpload: false,
        maxImageSize: 0,
        allowTextInput: true,
        maxTextLength: 800,
        allowFileUpload: false,
        maxFileSize: 0,
        gradingMode: 'ai',
        scoringPoints: '',
        explanation: '',
        answerText: '',
        scoringPointsImages: [],
        scoringPointsImageWidths: [],
        referenceAnswerImages: [],
        referenceAnswerImageWidths: []
      };
      setQuestions(sortQuestionsByType([...questions, newQuestion]));
      const sortedQs = sortQuestionsByType([...questions, newQuestion]);
      const newIdx = sortedQs.findIndex(q => q.id === newQuestion.id);
      if (newIdx >= 0) setCurrentIndex(newIdx);
      return;
    }

    if (typeItem.type === 'scratch_coding') {
      const subtype = typeItem.scratchSubtype || 'operation';
      const newQuestion: Question = {
        id: crypto.randomUUID(),
        type: 'scratch_coding',
        subType: 'scratch',
        title: '',
        content: '',
        placeholder: '请输入代码',
        required: true,
        inputType: '文本',
        options: [],
        score: 15,
        showAnswer: false,
        titleImages: [],
        titleImageWidths: [],
        answerImages: [],
        answerImageWidths: [],
        codeTemplate: '',
        codeLanguage: 'scratch',
        allowImageUpload: true,
        maxImageSize: 5,
        allowTextInput: false,
        maxTextLength: 0,
        allowFileUpload: false,
        maxFileSize: 0,
        gradingMode: 'ai',
        scoringPoints: '',
        explanation: '',
        answerText: '',
        scoringPointsImages: [],
        scoringPointsImageWidths: [],
        referenceAnswerImages: [],
        referenceAnswerImageWidths: [],
        scratchAnswerItems: [
          { id: '1', name: '程序截图', placeholder: '请上传程序截图', required: true, maxSizeMB: 5 }
        ],
        scratchSubtype: subtype,
        scratchConfig: null
      };
      setQuestions(sortQuestionsByType([...questions, newQuestion]));
      const sortedQs = sortQuestionsByType([...questions, newQuestion]);
      const newIdx = sortedQs.findIndex(q => q.id === newQuestion.id);
      if (newIdx >= 0) setCurrentIndex(newIdx);
      return;
    }

    const getDefaultScore = (type: string) => {
      switch (type) {
        case 'single_choice': return 2;
        case 'judge': return 2;
        case 'multi_choice': return 5;
        case 'coding': return 15;
        case 'scratch_coding': return 15;
        default: return 0;
      }
    };

    const newQuestion: Question = {
      id: crypto.randomUUID(),
      type: typeItem.type,
      title: '',
      placeholder: typeItem.placeholder,
      required: true,
      inputType: '文本',
      options: typeItem.type === 'single_choice' ? [
        { id: 'A', label: 'A', text: '', isCorrect: true },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false }
      ] : typeItem.type === 'judge' ? [
        { id: 'A', label: 'A', text: '正确', isCorrect: true },
        { id: 'B', label: 'B', text: '错误', isCorrect: false }
      ] : typeItem.type === 'multi_choice' ? [
        { id: 'A', label: 'A', text: '', isCorrect: false },
        { id: 'B', label: 'B', text: '', isCorrect: false },
        { id: 'C', label: 'C', text: '', isCorrect: false },
        { id: 'D', label: 'D', text: '', isCorrect: false }
      ] : [],
      score: getDefaultScore(typeItem.type),
      showAnswer: false,
      titleImages: [],
      titleImageWidths: [],
      codeTemplate: '',
      codeLanguage: typeItem.type === 'python_coding' ? 'python' : typeItem.type === 'cpp_coding' ? 'cpp' : '',
      allowFileUpload: false,
      maxFileSize: 10,
      gradingMode: 'ai',
      scoringPoints: '',
      answerText: '',
      answerImages: [],
      answerImageWidths: [],
      explanation: ''
    };
    setQuestions(sortQuestionsByType([...questions, newQuestion]));
    // 导航到新题目位置
    const sortedQuestions = sortQuestionsByType([...questions, newQuestion]);
    const newIndex = sortedQuestions.findIndex(q => q.id === newQuestion.id);
    if (newIndex >= 0) setCurrentIndex(newIndex);
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, ...updates } : q));
  };

  const updateOption = (questionId: string, optionId: string, updates: Partial<Option>) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: q.options.map((opt: any) =>
            opt.id === optionId ? { ...opt, ...updates } : opt
          )
        };
      }
      return q;
    }));
  };

  const setCorrectAnswer = (questionId: string, optionId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: q.options.map((opt: any) => ({
            ...opt,
            isCorrect: opt.id === optionId
          }))
        };
      }
      return q;
    }));
  };

  const toggleCorrectAnswer = (questionId: string, optionId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return {
          ...q,
          options: q.options.map((opt: any) =>
            opt.id === optionId ? { ...opt, isCorrect: !opt.isCorrect } : opt
          )
        };
      }
      return q;
    }));
  };

  const removeQuestion = (id: string) => {
    setQuestions(sortQuestionsByType(questions.filter(q => q.id !== id)));
    if (editingQuestion === id) setEditingQuestion(null);
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index - 1]] = [newQuestions[index - 1], newQuestions[index]];
      setQuestions(newQuestions);
    } else if (direction === 'down' && index < questions.length - 1) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      setQuestions(newQuestions);
    }
  };

  const handleDragStart = (e: React.DragEvent, question: Question, index: number) => {
    setDraggedQuestion(question);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedQuestion && questions[index].type === draggedQuestion.type) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));

    if (dragIndex === dropIndex) {
      setDraggedQuestion(null);
      setDragOverIndex(null);
      return;
    }

    const draggedQ = questions[dragIndex];
    const targetQ = questions[dropIndex];

    if (draggedQ.type !== targetQ.type) {
      setDraggedQuestion(null);
      setDragOverIndex(null);
      return;
    }

    const newQuestions = [...questions];
    newQuestions.splice(dragIndex, 1);
    newQuestions.splice(dropIndex, 0, draggedQ);
    setQuestions(newQuestions);
    setHasOrderChanges(true);
    setDraggedQuestion(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedQuestion(null);
    setDragOverIndex(null);
  };

  // 切换题目后滚动到题目区域顶部，确保题干可见
  // 根因已修复：4 个非客观题编辑器（Subjective/Python/Cpp/Scratch）内部的 sticky 导航栏已移除，
  // 浏览器不再因 sticky 元素挂载而自动滚动到底部。
  // 这里只需在 useLayoutEffect 中（浏览器绘制前）将题目区域滚动到顶部即可。
  const pendingScrollRef = useRef(false);
  const questionAreaRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    // 在浏览器绘制前立即滚动到题目区域顶部，用户看不到中间状态
    if (questionAreaRef.current) {
      questionAreaRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [currentIndex]);

  // Quiz风格预览模式题目切换
  const handlePrevQuestion = () => {
    if (currentIndex > 0) {
      pendingScrollRef.current = true;
      setCurrentIndex(prev => Math.max(0, prev - 1));
    }
  };

  const handleNextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      pendingScrollRef.current = true;
      setCurrentIndex(prev => Math.min(prev + 1, questions.length - 1));
    }
  };

  // 点击答题卡题号跳转
  const handleQuestionSelect = (index: number) => {
    pendingScrollRef.current = true;
    setCurrentIndex(() => index);
  };

  // 处理单个题目内容变更(全卷编辑模式)
  const handleQuestionChange = (index: number, updatedQuestion: any) => {
    setQuestions(prev => {
      const newQuestions = [...prev];
      newQuestions[index] = updatedQuestion;
      return newQuestions;
    });
  };

  // 保存宽度设置到数据库
  const handleSaveWidthSettings = async () => {
    if (!editPaperId || !hasWidthChanges) return;
    setIsSavingWidths(true);
    try {
      for (const q of questions) {
        await supabase.from(questionsTable).update({
          title_image_widths: q.titleImageWidths,
          option_image_widths: q.options?.reduce((acc: Record<string, number>, o) => {
            if (o.image && o.imageWidth) acc[o.id] = o.imageWidth;
            return acc;
          }, {}) || {},
          scoring_points_image_widths: q.scoringPointsImageWidths || [],
          reference_answer_image_widths: q.referenceAnswerImageWidths || []
        }).eq('id', q.id);
      }
      setHasWidthChanges(false);
    } catch (err) {
      console.error('保存宽度设置失败:', err);
    } finally {
      setIsSavingWidths(false);
    }
  };

  const duplicateQuestion = (question: Question) => {
    const newQuestion = {
      ...question,
      id: crypto.randomUUID(),
      title: question.title + ' (复制)'
    };
    setQuestions(sortQuestionsByType([...questions, newQuestion]));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: string[] = [];
    let processedCount = 0;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        newImages.push(event.target?.result as string);
        processedCount++;
        if (processedCount === files.length) {
          setUploadedImages([...uploadedImages, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const getImageUrl = (img: unknown): string => {
    if (typeof img !== 'string' || !img) return '';
    if (img.startsWith('/sb-api/')) return img;
    if (img.startsWith('/storage/')) return `${getSupabaseUrl()}${img}`;
    return img;
  };

  // 构建答题卡数据（复用现有逻辑）
  const answerCardGroups = useMemo(() => {
    const groups: { type: string; name: string; questions: Question[] }[] = [];
    const typeOrder = ['single_choice', 'judge', 'multi_choice', 'python_coding', 'cpp_coding', 'scratch_coding'];
    const typeNamesMap: Record<string, string> = {
      single_choice: '单选题',
      judge: '判断题',
      multi_choice: '多选题',
      python_coding: 'Python编程题',
      cpp_coding: 'C++编程题',
      scratch_coding: 'Scratch编程题'
    };
    const typeLabels = ['一', '二', '三', '四', '五', '六'];
    const defaultScores: Record<string, number> = {
      single_choice: 2, judge: 2, multi_choice: 3,
      python_coding: 15, cpp_coding: 15, scratch_coding: 15
    };
    const scoreOptions = [1, 2, 3, 5, 10, 15, 20];

    typeOrder.forEach((type) => {
      const typeQuestions = questions.filter(q => q.type === type);
      if (typeQuestions.length > 0) {
        groups.push({ type, name: typeNamesMap[type], questions: typeQuestions });
      }
    });

    const otherQuestions = questions.filter(q => !typeOrder.includes(q.type));
    if (otherQuestions.length > 0) {
      groups.push({ type: 'other', name: '其他题型', questions: otherQuestions });
    }

    return { groups, typeLabels, defaultScores, scoreOptions };
  }, [questions]);

  const updateTypeScore = (type: string, score: number) => {
    setQuestions(questions.map(q => q.type === type ? { ...q, score } : q));
  };

  // 确保 currentIndex 不超出题目范围
  const safeCurrentIndex = currentIndex >= questions.length ? Math.max(0, questions.length - 1) : currentIndex;

  // 当前题目
  const currentQuestion = questions[safeCurrentIndex];

  // 获取题型标签
  const getQuestionTypeName = (type?: string, subType?: string) => {
    if (!type) return '单选题';
    const typeMap: Record<string, string> = {
      single_choice: '单选题',
      multi_choice: '多选题',
      judge: '判断题',
      subjective: '主观题',
      python_coding: 'Python编程题',
      cpp_coding: 'C++编程题',
      scratch_coding: 'Scratch编程题',
    };
    return typeMap[type] || '单选题';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - 与 QuizAnswerMode 完全一致 */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 lg:h-24 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            <button onClick={() => {
              // 根据来源页面动态设置返回目标
              if (fromPage === 'pending') {
                navigate('/admin/pending-papers');
              } else if (fromPage === 'papers') {
                navigate('/admin/papers');
              } else {
                navigate(actualIsTeacherMode ? '/teacher/papers' : '/admin/papers');
              }
            }} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              <span className="text-xs lg:text-sm">返回</span>
            </button>
            <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <Home className="w-4 h-4" />
              <span className="text-xs lg:text-sm hidden sm:inline">首页</span>
            </button>
          </div>
          <div className="flex flex-col items-center min-w-0 px-2">
            <h1 className="font-bold text-gray-900 text-sm sm:text-base lg:text-2xl truncate max-w-[200px] sm:max-w-[400px] lg:max-w-none">
              {title || '试卷编辑'}
            </h1>
          </div>
          <div className="flex items-center gap-2 lg:gap-4 shrink-0">
            {/* 倒计时 - 置灰显示 */}
            <div className="flex items-center gap-1 lg:gap-2 px-2 lg:px-4 py-1 lg:py-2 rounded-lg border bg-gray-100 border-gray-300 text-gray-400 opacity-60">
              <Clock className="w-3 h-3 lg:w-4 lg:h-4" />
              <span className="font-mono font-bold text-xs lg:text-base">
                01:00:00
              </span>
            </div>
            {/* 试卷设置按钮 */}
            <button
              onClick={() => setShowPaperSettingsModal(true)}
              className="px-6 py-2 rounded-lg font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              试卷设置
            </button>
          </div>
        </div>
      </header>

      {/* 主内容区 - 两栏布局：左Quiz页面 + 右配置面板 */}
      <div className="flex flex-col bg-gray-50">
        {/* 左栏：Quiz答题页面（与真实答题页面100%一致） */}
        <div className="flex-1 bg-gray-50">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-6">
            <div className="flex gap-4 lg:gap-6">
              {/* 答题卡区域 - 固定不动 */}
              <div className="hidden lg:block w-[calc(25%-1.5rem)] shrink-0">
                <div className="sticky top-[calc(4rem+1px)]">
                <AnswerCard
                  examQuestions={questions as any}
                  currentIndex={safeCurrentIndex}
                  answers={{}}
                  markedQuestions={new Set<number>()}
                  isSubmitted={false}
                  paper={{
                    id: editPaperId || pendingPaperId || '',
                    title: title || '试卷编辑',
                    total_score: questions.reduce((sum, q) => sum + (q.score || 0), 0),
                    category_type: '',
                  }}
                  onQuestionSelect={(index: number) => {
                    handleQuestionSelect(index);
                  }}
                  onGroupScoreChange={(groupType: string, newScorePerQuestion: number) => {
                    // 根据题型找到对应的题目并更新分值
                    const updatedQuestions = questions.map(q => {
                      let qGroupType = q.type;
                      if (q.type === 'coding' && q.subType) qGroupType = `${q.subType}_coding`;
                      if (qGroupType === groupType) {
                        return { ...q, score: newScorePerQuestion };
                      }
                      return q;
                    });
                    setQuestions(updatedQuestions);
                  }}
                />
                </div>
              </div>

              {/* 题目区域 */}
              <div
                ref={questionAreaRef}
                className="flex-1 min-w-0 space-y-3 sm:space-y-4"
                data-question-editor-area
                style={{ overflowAnchor: 'none' }}
              >
                {questions.length > 0 ? (
                  /* 始终渲染当前题目的编辑界面 */
                  currentQuestion ? (
                      <QuizStyleQuestionView
                        key={`edit-${currentQuestion.id}`}
                        question={currentQuestion}
                        paperId={editPaperId || pendingPaperId || undefined}
                        currentIndex={safeCurrentIndex}
                        totalCount={questions.length}
                        examQuestions={questions}
                        onPrevQuestion={handlePrevQuestion}
                        onNextQuestion={handleNextQuestion}
                        onExamQuestionsChange={setQuestions}
                        hasWidthChanges={hasWidthChanges}
                        onSaveWidthSettings={handleSaveWidthSettings}
                        isSavingWidths={isSavingWidths}
                        showNavigation={true}
                        onSaveQuestion={async (updatedQuestion) => {
                            const updatedQuestions = questions.map(q => q.id === updatedQuestion.id ? updatedQuestion : q);
                            setQuestions(updatedQuestions);
                            if (isUUID(updatedQuestion.id)) {
                              try {
                                // 按 type 分发特化字段写入
                                if (updatedQuestion.type === 'subjective') {
                                  // 主观题特化字段：字符限制、评分模式
                                  await supabase.from(questionsTable).update({
                                    title: updatedQuestion.title || '',
                                    content: updatedQuestion.content || '',
                                    reference_answer: updatedQuestion.referenceAnswer || updatedQuestion.answerText || null,
                                    reference_answer_images: extractImagesFromHtml(updatedQuestion.referenceAnswer || '').urls,
                                    reference_answer_image_widths: extractImagesFromHtml(updatedQuestion.referenceAnswer || '').widths,
                                    scoring_points: updatedQuestion.scoringPoints || null,
                                    scoring_points_images: extractImagesFromHtml(updatedQuestion.scoringPoints || '').urls,
                                    scoring_points_image_widths: extractImagesFromHtml(updatedQuestion.scoringPoints || '').widths,
                                    title_image_widths: updatedQuestion.titleImageWidths || [],
                                    max_text_length: updatedQuestion.maxTextLength ?? null,
                                    min_text_length: updatedQuestion.minTextLength ?? null,
                                    grading_mode: updatedQuestion.gradingMode || 'ai',
                                    updated_at: new Date().toISOString(),
                                  }).eq('id', updatedQuestion.id);
                                } else {
                                  // 客观题 / python / cpp / scratch 通用字段
                                  await supabase.from(questionsTable).update({
                                    title: updatedQuestion.title || '',
                                    content: updatedQuestion.content || '',
                                    // 保存 options 数组（包含 option.imageWidths 等图片宽度信息）
                                    // 不保存会导致选项图片缩放值丢失
                                    options: updatedQuestion.options || [],
                                    reference_answer: updatedQuestion.referenceAnswer || null,
                                    reference_answer_images: extractImagesFromHtml(updatedQuestion.referenceAnswer || '').urls,
                                    reference_answer_image_widths: extractImagesFromHtml(updatedQuestion.referenceAnswer || '').widths,
                                    scoring_points: updatedQuestion.scoringPoints || null,
                                    scoring_points_images: extractImagesFromHtml(updatedQuestion.scoringPoints || '').urls,
                                    scoring_points_image_widths: extractImagesFromHtml(updatedQuestion.scoringPoints || '').widths,
                                    title_image_widths: updatedQuestion.titleImageWidths || [],
                                    title_image_layout: updatedQuestion.titleImageLayout || null,
                                    image_position: updatedQuestion.imagePosition || null,
                                    option_layout: updatedQuestion.optionLayout || null,
                                    option_image_widths: updatedQuestion.optionImageWidths || {},
                                    reference_sb3_url: updatedQuestion.referenceSb3Url || null,
                                    updated_at: new Date().toISOString(),
                                  }).eq('id', updatedQuestion.id);
                                }
                              } catch (err) { console.error('保存题目到数据库失败:', err); }
                            }
                          }}
                        onCancelEdit={() => {
                          // 取消修改：子组件内部已处理 editDraft 重置
                        }}
                        onDeleteQuestion={(questionId) => removeQuestion(questionId)}
                        onInsertQuestion={(questionId) => { setInsertTargetQuestionId(questionId); setShowInsertModal(true); }}
                        onCopyQuestion={(questionId) => { setCopyTargetQuestionId(questionId); setShowCopyConfirmModal(true); }}
                        onSaveQuestionLayout={handleSaveQuestionLayout}
                      />
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                      请从右侧添加题目
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </div>

          {/* 右栏：配置面板 */}
          <div className="w-80 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
            {/* 空占位 - 题型选择已移至右侧悬浮工具栏 */}
          </div>
        </div>
      </div>

      {imageUploadTarget && (
        <ImageUploadModal
          target={imageUploadTarget}
          onClose={() => setImageUploadTarget(null)}
          onUpload={(imageData) => {
            const { questionId, optionId } = imageUploadTarget;
            if (optionId) {
              updateOption(questionId, optionId, { image: imageData, imageWidth: 100 });
            } else {
              const newImages = [...(questions.find(q => q.id === questionId)?.titleImages || []), imageData];
              const newWidths = [...(questions.find(q => q.id === questionId)?.titleImageWidths || []), 50];
              updateQuestion(questionId, { titleImages: newImages, titleImageWidths: newWidths });
            }
            setImageUploadTarget(null);
          }}
          allowMultiple={!imageUploadTarget.optionId}
        />
      )}

      {showImageUploadDialog && imageUploadDialogConfig && (
        <ImageUploadDialog
          isOpen={showImageUploadDialog}
          onClose={() => setShowImageUploadDialog(false)}
          title={imageUploadDialogConfig.title}
          maxCount={10}
          initialImages={(() => {
            const question = questions.find(q => q.id === imageUploadDialogConfig.questionId);
            if (!question) return [];
            switch (imageUploadDialogConfig.target) {
              case 'title': return question.titleImages || [];
              case 'answer': return question.answerImages || [];
              case 'scoringPoints': return question.scoringPointsImages || [];
              case 'referenceAnswer': return question.referenceAnswerImages || [];
              default: return [];
            }
          })()}
          onConfirm={(images) => {
            const question = questions.find(q => q.id === imageUploadDialogConfig.questionId);
            if (!question) return;
            const widths = images.map(() => 50);
            switch (imageUploadDialogConfig.target) {
              case 'title':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  titleImages: images,
                  titleImageWidths: widths
                });
                break;
              case 'answer':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  answerImages: images,
                  answerImageWidths: widths
                });
                break;
              case 'scoringPoints':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  scoringPointsImages: images,
                  scoringPointsImageWidths: widths
                });
                break;
              case 'referenceAnswer':
                updateQuestion(imageUploadDialogConfig.questionId, {
                  referenceAnswerImages: images,
                  referenceAnswerImageWidths: widths
                });
                break;
            }
            setShowImageUploadDialog(false);
          }}
        />
      )}

      {showSaveConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                {saveConfirmAction === 'draft' && '暂存为草稿'}
                {saveConfirmAction === 'saved' && '保存为试卷'}
                {saveConfirmAction === 'published' && '保存并发布'}
              </h3>
              <button onClick={() => setShowSaveConfirmModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              {saveConfirmAction === 'draft' && (
                <div className="space-y-2">
                  <p className="text-gray-700">确认将试卷暂存为草稿？</p>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                    <p>保存后状态：草稿</p>
                    <p>可见性：仅管理员可见，不会出现在正式试卷列表</p>
                    <p>用途：方便后续继续编辑</p>
                  </div>
                </div>
              )}
              {saveConfirmAction === 'saved' && (
                <div className="space-y-2">
                  <p className="text-gray-700">确认将试卷保存为发布状态？</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    <p>保存后状态：发布</p>
                    <p>可见性：教师可在发布作业时选择此试卷</p>
                    <p>用途：已发布的试卷可用于布置作业</p>
                  </div>
                </div>
              )}
              {saveConfirmAction === 'published' && (
                <div className="space-y-2">
                  <p className="text-gray-700">确认保存并发布试卷？</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    <p>保存后状态：已发布</p>
                    <p>可见性：学员端可见，可正常答题</p>
                    <p>注意：发布后学员即可看到并参与考试</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSaveConfirmModal(false)}
                disabled={savingAction === saveConfirmAction}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setShowSaveConfirmModal(false);
                  if (saveConfirmAction === 'draft') {
                    saveAsDraft();
                  } else {
                    savePaper(saveConfirmAction);
                  }
                }}
                disabled={savingAction === saveConfirmAction}
                className={`flex-1 px-4 py-2 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 ${
                  saveConfirmAction === 'draft' ? 'bg-orange-500 hover:bg-orange-600' :
                  saveConfirmAction === 'saved' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {savingAction === saveConfirmAction ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                    保存中...
                  </>
                ) : (
                  `确认${saveConfirmAction === 'draft' ? '暂存' : saveConfirmAction === 'saved' ? '保存' : '发布'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-red-600">清空确认</h3>
              <button onClick={() => setShowClearConfirmModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-red-600">
                  <AlertCircle className="w-6 h-6" />
                  <p className="font-medium">确定要清空所有题目吗？</p>
                </div>
                <p className="text-gray-600 text-sm">此操作将删除当前编辑中的所有题目（共 {questions.length} 道），此操作不可撤销。</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  <p>注意：试卷设置信息（年份、考评体系等）将保留不变</p>
                  <p>已保存的草稿或已发布的试卷数据不受影响</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirmModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={() => {
                  setQuestions([]);
                  setEditingQuestion(null);
                  setShowClearConfirmModal(false);
                }}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 插入题目弹窗 */}
      {showInsertModal && insertTargetQuestionId && questions.find(q => q.id === insertTargetQuestionId) && (
        <InsertQuestionModal
          isOpen={showInsertModal}
          currentQuestion={questions.find(q => q.id === insertTargetQuestionId)!}
          currentIndex={currentIndex}
          totalQuestions={questions.length}
          onConfirm={handleInsertConfirm}
          onCancel={() => {
            setShowInsertModal(false);
            setInsertTargetQuestionId(null);
          }}
        />
      )}

      {/* 复制题目确认弹窗 */}
      <AnimatePresence>
        {showCopyConfirmModal && copyTargetQuestionId && (() => {
          const targetQuestion = questions.find(q => q.id === copyTargetQuestionId);
          if (!targetQuestion) return null;
          const targetIndex = questions.findIndex(q => q.id === copyTargetQuestionId);
          const typeName = getQuestionTypeLabel(targetQuestion.type, targetQuestion.scratchSubtype);
          return (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => { setShowCopyConfirmModal(false); setCopyTargetQuestionId(null); }}
                className="fixed inset-0 bg-black/50 z-50"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
              >
                <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 pointer-events-auto">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">确认复制题目</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        当前第 {targetIndex + 1} 题（共 {questions.length} 题）
                      </p>
                    </div>
                    <button
                      onClick={() => { setShowCopyConfirmModal(false); setCopyTargetQuestionId(null); }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                  <div className="px-6 py-5 space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-2">将复制当前题目并插入到本题之后：</p>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          第 {targetIndex + 1} 题
                        </span>
                        <span className="text-sm text-gray-700">
                          {typeName} · {targetQuestion.score}分
                        </span>
                      </div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        <span className="font-medium">提示：</span>
                        复制的题目将包含本题的所有内容（题干、选项、分值等），成为第 {targetIndex + 2} 题，创建后可直接编辑。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                    <button
                      onClick={() => { setShowCopyConfirmModal(false); setCopyTargetQuestionId(null); }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleCopyConfirm}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      确认复制
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          );
        })()}
      </AnimatePresence>

      {/* 试卷预览模态框 */}
      <AnimatePresence>
        {showPreviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={() => setShowPreviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <QuizAnswerMode
                paper={{
                  id: editPaperId || pendingPaperId || '',
                  title: title || '试卷预览',
                  total_score: questions.reduce((sum, q) => sum + q.score, 0),
                  category_type: '',
                  description: description || '',
                  remark: remark || '',
                  year: null,
                  month: null,
                  organization: null,
                  source: null,
                  nature: null,
                  level_text: null,
                  paper_type: null,
                  duration: 60
                }}
                examQuestions={questions}
                currentUser={null}
                paperId={editPaperId || pendingPaperId || undefined}
                isPreview={true}
                isEditorPreview={true}
                canScaleImages={true}
                onBack={() => setShowPreviewModal(false)}
                onExamQuestionsChange={setQuestions}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 右侧固定悬浮工具栏 - 始终垂直排列贴右边缘 */}
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-1.5">
        {/* 全卷预览 / 退出预览 */}
        <button
            onClick={() => {
              setShowPreviewModal(!showPreviewModal);
            }}
            disabled={isSaving || questions.length === 0}
            className={`h-10 px-3 rounded-l-lg transition-colors flex items-center justify-start gap-1.5 shadow-lg disabled:opacity-50 whitespace-nowrap ${
              showPreviewModal
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-purple-500 text-white hover:bg-purple-600'
            }`}
            title={showPreviewModal ? "退出预览" : "全卷预览"}
          >
            <Eye className="w-4 h-4 shrink-0" />
            <span className="text-sm font-medium">{showPreviewModal ? "退出预览" : "全卷预览"}</span>
          </button>
        {/* 清空题目 */}
        <button
          onClick={() => setShowClearConfirmModal(true)}
          disabled={isSaving}
          className="h-10 px-3 bg-red-500 text-white rounded-l-lg hover:bg-red-600 transition-colors flex items-center justify-start gap-1.5 shadow-lg disabled:opacity-50 whitespace-nowrap"
          title="清空题目"
        >
          <Eraser className="w-4 h-4 shrink-0" />
          <span className="text-sm font-medium">清空题目</span>
        </button>
        {/* 排序未保存提示 */}
        {hasOrderChanges && (
          <div className="h-10 px-3 bg-amber-50 text-amber-700 rounded-l-lg flex items-center gap-1.5 border border-amber-200 whitespace-nowrap">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span className="text-sm font-medium">排序未保存</span>
          </div>
        )}
        {/* 暂存为草稿 */}
        <button
          onClick={() => { setSaveConfirmAction('draft'); setShowSaveConfirmModal(true); }}
          disabled={savingAction === 'draft'}
          className="h-10 px-3 bg-orange-500 text-white rounded-l-lg hover:bg-orange-600 transition-colors flex items-center justify-start gap-1.5 shadow-lg disabled:opacity-50 whitespace-nowrap"
          title="暂存为草稿"
        >
          {savingAction === 'draft' ? (
            <svg className="w-4 h-4 animate-spin shrink-0" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
          ) : (
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
          )}
          <span className="text-sm font-medium">{savingAction === 'draft' ? '保存中...' : '暂存为草稿'}</span>
        </button>
        {/* 保存为试卷 */}
        <button
          onClick={() => savePaper('saved')}
          disabled={savingAction === 'saved'}
          className="h-10 px-3 bg-blue-500 text-white rounded-l-lg hover:bg-blue-600 transition-colors flex items-center justify-start gap-1.5 shadow-lg disabled:opacity-50 whitespace-nowrap"
          title="保存为试卷"
        >
          {savingAction === 'saved' ? (
            <svg className="w-4 h-4 animate-spin shrink-0" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
          ) : (
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
          )}
          <span className="text-sm font-medium">{savingAction === 'saved' ? '保存中...' : '保存为试卷'}</span>
        </button>
        {/* 保存并发布 */}
        {!actualIsTeacherMode && (
          <button
            onClick={() => { setSaveConfirmAction('published'); setShowSaveConfirmModal(true); }}
            disabled={savingAction === 'published'}
            className="h-10 px-3 bg-green-500 text-white rounded-l-lg hover:bg-green-600 transition-colors flex items-center justify-start gap-1.5 shadow-lg disabled:opacity-50 whitespace-nowrap"
            title="保存并发布"
          >
            {savingAction === 'published' ? (
              <svg className="w-4 h-4 animate-spin shrink-0" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
            ) : (
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            )}
            <span className="text-sm font-medium">{savingAction === 'published' ? '发布中...' : '保存并发布'}</span>
          </button>
        )}
        {/* 一键发布（待发布试卷） */}
        {isPendingPaper && pendingPaperId && (
          <button
            onClick={publishPendingPaper}
            disabled={savingAction === 'published'}
            className="h-10 px-3 bg-green-600 text-white rounded-l-lg hover:bg-green-700 transition-colors flex items-center justify-start gap-1.5 shadow-lg disabled:opacity-50 whitespace-nowrap"
            title="一键发布"
          >
            {savingAction === 'published' ? (
              <svg className="w-4 h-4 animate-spin shrink-0" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
            ) : (
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            )}
            <span className="text-sm font-medium">{savingAction === 'published' ? '发布中...' : '一键发布'}</span>
          </button>
        )}
        {/* 题型选择区 - 始终显示 */}
        <div className="bg-white rounded-l-lg shadow-lg border border-gray-200 overflow-hidden mt-4">
          {questionTypes.map((category) => (
            <div key={category.category}>
              {category.items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={`${item.type}-${item.scratchSubtype || 'default'}`}
                    onClick={() => addQuestion(item)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* 试卷属性弹窗 */}
      {showPaperSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                试卷设置
              </h3>
              <button onClick={() => setShowPaperSettingsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {actualIsTeacherMode ? (
                // 教师模式：只显示试卷名称、备注、考试时长
                <>
                  <div className="flex items-start gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0 pt-2">试卷名称:</span>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="请输入试卷名称"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0 pt-2">备注:</span>
                    <textarea
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      placeholder="请输入备注信息"
                      rows={3}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0">考试时长:</span>
                    <select value={paperSettings.duration} onChange={(e) => setPaperSettings({...paperSettings, duration: e.target.value})} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500">
                      <option value="">请选择</option>
                      <option value="30">30分钟</option>
                      <option value="60">60分钟</option>
                      <option value="90">90分钟</option>
                      <option value="120">120分钟</option>
                      <option value="150">150分钟</option>
                      <option value="180">180分钟</option>
                    </select>
                  </div>
                </>
              ) : (
                // 后台管理模式：显示完整属性
                <>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0">年份:</span>
                    <select value={paperSettings.year} onChange={(e) => setPaperSettings({...paperSettings, year: e.target.value})} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500">
                      <option value="">请选择</option>
                      {years.filter(y => y).map(year => <option key={year} value={year}>{year}年</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0">月份:</span>
                    <select value={paperSettings.month} onChange={(e) => setPaperSettings({...paperSettings, month: e.target.value})} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500">
                      <option value="">请选择</option>
                      <option value="0">空</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(month => <option key={month} value={String(month)}>{month}月</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0">考评体系:</span>
                    <select value={paperSettings.organization} onChange={(e) => setPaperSettings({...paperSettings, organization: e.target.value, categoryType: '', level_text: ''})} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500">
                      <option value="">请选择</option>
                      {orgs.map(org => <option key={org.id} value={org.name}>{org.name}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0">课程分类:</span>
                    <select value={paperSettings.categoryType} onChange={(e) => setPaperSettings({...paperSettings, categoryType: e.target.value, level_text: ''})} disabled={!paperSettings.organization} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
                      <option value="">请选择</option>
                      {filteredCats.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                    </select>
                  </div>
                  {!actualIsTeacherMode && (
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0">级别分类:</span>
                    <select value={paperSettings.level_text} onChange={(e) => setPaperSettings({...paperSettings, level_text: e.target.value})} disabled={!paperSettings.categoryType} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
                      <option value="">请选择</option>
                      {filteredLevels.map(level => (<option key={level.id} value={level.name}>{level.name}</option>))}
                    </select>
                  </div>
                  )}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0">试卷类型:</span>
                    <select value={paperSettings.source} onChange={(e) => setPaperSettings({...paperSettings, source: e.target.value})} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500">
                      <option value="">请选择</option>
                      <option value="真题">真题</option>
                      <option value="模拟题">模拟题</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0">免费/付费:</span>
                    <select value={paperSettings.nature} onChange={(e) => setPaperSettings({...paperSettings, nature: e.target.value})} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500">
                      <option value="">请选择</option>
                      {natures.map(nature => <option key={nature} value={nature}>{nature}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 w-20 shrink-0">考试时长:</span>
                    <select value={paperSettings.duration} onChange={(e) => setPaperSettings({...paperSettings, duration: e.target.value})} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500">
                      <option value="">请选择</option>
                      <option value="30">30分钟</option>
                      <option value="60">60分钟</option>
                      <option value="90">90分钟</option>
                      <option value="120">120分钟</option>
                      <option value="150">150分钟</option>
                      <option value="180">180分钟</option>
                    </select>
                  </div>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowPaperSettingsModal(false)}
                className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                取消
              </button>
              <button
                onClick={async () => {
                  // 保存试卷属性到数据库
                  if (editPaperId) {
                    const { error } = await supabase.from(papersTable).update({
                      title,
                      remark,
                      duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
                    }).eq('id', editPaperId);
                    if (error) {
                      alert('保存失败: ' + error.message);
                      return;
                    }
                  } else if (pendingPaperId) {
                    const { error } = await supabase.from(pendingPapersTable).update({
                      title,
                      remark,
                      duration: paperSettings.duration ? parseInt(paperSettings.duration) : 60,
                    }).eq('id', pendingPaperId);
                    if (error) {
                      alert('保存失败: ' + error.message);
                      return;
                    }
                  }
                  setShowPaperSettingsModal(false);
                }}
                className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
